// @ts-nocheck -- Hand-written vanilla ES module for Shopify (no TypeScript build).
// The editor's TS language server otherwise floods this valid JS file with
// implicit-any / strict-null false positives. Runtime correctness is covered by
// `node --check` and tests/training-booking-theme-contract.test.mjs.
/*
 * IdenScan Training Booking Engine
 * Version 5.0.0
 * Matches sections/idenscan-native-booking.liquid
 * Flow: package -> availability -> preparation -> booking intent -> Stripe Checkout Session
 */
(() => {
  'use strict';

  const VERSION = '5.0.0';
  const ROOT_SELECTOR = '[data-idenscan-booking-v4]';
  const GLOBAL_GUARD = '__IDENSCAN_TRAINING_BOOKING_V5__';
  const MAX_PRIORITIES = 2;
  const REQUEST_TIMEOUT_MS = 15_000;

  // Step-3 guided intake answers. These never affect the chosen package, the
  // route, availability, pricing, the summary or any location/contact rule — the
  // payload reads them straight from the form at submit time, and native CSS
  // (:checked) already renders the selection instantly. So on change/input we can
  // skip the full recalc pipeline for these fields, which keeps taps snappy on
  // mobile. `priorities` is deliberately EXCLUDED (it feeds the summary + limit).
  const LIGHT_INTAKE_FIELDS = new Set([
    'incident_experience',
    'reporting_route',
    'data_types',
    'data_types_other',
    'ai_usage',
    'ai_tools',
    'ai_tools_other',
    'business_systems',
    'business_systems_other',
    'daily_tools',
    'daily_tools_other',
    'payment_control',
    'training_goal'
  ]);
  const FORBIDDEN_CYBERCHECK_PAYMENT_LINK_IDS = Object.freeze([
    'fZueVe7VE3pD5e04GX3ks0c',
    'bJeeVegsa6BP9ug6P53ks0d',
    'cNi8wQ7VE0drgWIb5l3ks0e'
  ]);
  const instances = new WeakMap();

  if (window[GLOBAL_GUARD]) {
    return;
  }
  window[GLOBAL_GUARD] = true;

  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => (root ? Array.from(root.querySelectorAll(selector)) : []);
  const int = (value, fallback = 0) => {
    const parsed = Number.parseInt(String(value ?? ''), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const isField = (value) => (
    value instanceof HTMLInputElement
    || value instanceof HTMLSelectElement
    || value instanceof HTMLTextAreaElement
  );
  const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const unique = (values) => Array.from(new Set(values));

  class BookingError extends Error {
    constructor(code, message, details = {}) {
      super(message);
      this.name = 'BookingError';
      this.code = code;
      this.details = details;
    }
  }

  class IdenScanTrainingBookingV5 {
    constructor(root) {
      this.root = root;
      this.form = q(root, '[data-booking-form]');
      this.steps = qa(root, '[data-step]');
      this.lifecycle = new AbortController();
      this.requestControllers = new Set();
      this.ready = false;
      // Fail closed immediately: block the native Shopify contact-form submission
      // (which would otherwise reload /pages/training-boeken with empty contact[...]
      // params and never reach Stripe) until the engine is fully initialised. The
      // real submit handler in bindEvents does the backend POST + Stripe redirect;
      // this capture-phase guard also covers any initialisation failure below.
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          if (!this.ready) event.preventDefault();
        }, { capture: true, signal: this.lifecycle.signal });
      }
      this.config = this.readConfig();
      this.valid = Boolean(this.form && this.config && this.steps.length === 4);
      this.currentStep = 1;
      this.submitLocked = false;
      this.nativeSubmitting = false;
      this.toastTimer = null;
      this.draftTimer = null;
      this.contextPromise = null;
      this.lastAvailabilityAt = 0;
      this.availabilityStatus = 'idle';
      this.slots = [];
      // Optional remote availability (GET /api/availability). When `success`, its
      // keys are used as an extra filter over the locally generated agenda; idle/
      // error states simply leave the local agenda untouched (never fail closed).
      this.remoteAvailability = { status: 'idle', ok: false, keys: null, at: 0 };
      this.slotGroups = [];
      this.slotGroupIndex = 0;
      this.calendarView = 'month';
      this.calendarCursor = null;
      this.selectedDate = '';
      this.availableDates = new Set();
      this.bookingWindow = null;
      this.tracked = new Set();
      this.state = {
        package: null,
        route: 'direct',
        routeReason: 'direct',
        slot: null,
        context: null,
        paymentMethod: '',
        bookingIntentId: '',
        bookingReference: '',
        // Set to the previous reference when the visitor changes their slot/package
        // after a hold was created, so the backend releases the old hold + session.
        supersedes: ''
      };

      if (!this.valid) {
        this.failClosed('We konden deze stap niet afronden. Vernieuw de pagina of neem contact met IdenScan op.');
        return;
      }

      try {
        this.setupBaseState();
        this.bindEvents();

        this.applyUrlDefaults();
        this.captureState();
        this.applyRoute({ announce: false });
        this.updateSelections();
        this.updatePriorityCount();
        this.updateProgress();
        this.updateSummary();
        this.updateSubmitState();
        this.trackView();
      } catch (error) {
        // Never leave a half-initialised, natively-submittable form behind.
        console.error('[IdenScan booking v5] init failed:', error?.message || 'unknown');
        this.failClosed('We konden deze stap niet afronden. Vernieuw de pagina of neem contact met IdenScan op.');
        return;
      }

      // Engine is ready: the guard above now defers to the real submit handler.
      this.ready = true;
      void this.loadCheckContext();
      // Best-effort remote availability refresh. The locally generated agenda is
      // already visible/bookable; a successful response only refines it further.
      void this.refreshRemoteAvailability();

      this.root.dataset.bookingInitialized = 'true';
      this.dispatch('ready', {
        version: VERSION,
        packageId: this.state.package?.id || null,
        route: this.state.route
      });
    }

    readConfig() {
      const node = q(this.root, '[data-booking-config]');
      if (!node) {
        console.error('[IdenScan booking v5] Configuratie ontbreekt.');
        return null;
      }

      let raw;
      try {
        raw = JSON.parse(node.textContent || '{}');
      } catch (error) {
        console.error('[IdenScan booking v5] Configuratie is geen geldige JSON.', error);
        return null;
      }

      const packages = {};
      Object.entries(raw.packages || {}).forEach(([key, value]) => {
        if (!isObject(value)) return;
        const id = String(value.id || key).trim();
        const route = value.route === 'intake' ? 'intake' : 'direct';
        const totalExVat = Math.max(0, int(value.totalExVat));
        const depositExVat = Math.max(0, int(value.depositExVat));
        const remainingExVat = Math.max(0, int(value.remainingExVat));
        const estimatedCheckoutInclVat = Math.max(0, int(value.estimatedCheckoutInclVat));
        const arithmeticValid = route === 'intake' || (
          totalExVat > 0
          && depositExVat > 0
          && remainingExVat === totalExVat - depositExVat
          && estimatedCheckoutInclVat >= depositExVat
        );

        packages[id] = Object.freeze({
          id,
          name: String(value.name || id),
          // Customer-facing name (e.g. "IdenScan Team"); the technical `name`
          // token (e.g. "IdenScan Teamtraining") stays intact for the backend.
          displayName: String(value.displayName || value.name || id),
          route,
          // Every package uses the SAME Step-2 calendar; only the reserved
          // duration differs. `route` still governs submit + payment surface.
          usesCalendar: value.usesCalendar !== false,
          durationMinutes: Math.max(0, int(value.durationMinutes)),
          recommendedParticipantsMin: Math.max(1, int(value.recommendedParticipantsMin, 1)),
          recommendedParticipantsMax: Math.max(1, int(value.recommendedParticipantsMax, 25)),
          totalExVat,
          depositExVat,
          remainingExVat,
          estimatedCheckoutInclVat,
          includesLegislationQuickCheck: Boolean(value.includesLegislationQuickCheck),
          ready: arithmeticValid
        });
      });

      if (!Object.keys(packages).length) {
        console.error('[IdenScan booking v5] Er zijn geen geldige pakketten geconfigureerd.');
        return null;
      }

      const api = raw.api || {};
      const slots = this.parseConfigSlots(raw.slots);
      const availability = this.parseAvailabilityConfig(raw.availability);
      const stripe = raw.stripe || {};
      const analytics = raw.analytics || {};
      const events = analytics.events || {};

      return Object.freeze({
        version: int(raw.version, 5),
        sectionId: String(raw.sectionId || this.root.dataset.sectionId || ''),
        currency: /^[A-Z]{3}$/.test(String(raw.currency || '').toUpperCase())
          ? String(raw.currency).toUpperCase()
          : 'EUR',
        vatRate: Math.max(0, int(raw.vatRate, 21)),
        maxDirectParticipants: Math.max(1, int(raw.maxDirectParticipants, 25)),
        freeCheckUrl: this.normalizePublicUrl(raw.freeCheckUrl || ''),
        freeCheckResponseText: String(raw.freeCheckResponseText || ''),
        contactEmail: String(raw.contactEmail || '').trim(),
        // GET /api/availability is an optional refinement over the locally
        // generated agenda: when reachable its keys filter the visible slots,
        // but API/config/schema errors never remove the local agenda.
        api: Object.freeze({
          booking: this.normalizeEndpoint(api.booking || ''),
          checkout: this.normalizeEndpoint(api.checkout || ''),
          invoice: this.normalizeEndpoint(api.invoice || ''),
          availability: this.normalizeEndpoint(api.availability || ''),
          context: this.normalizeEndpoint(api.context || '')
        }),
        // `slots` are training_slot block overrides; the day-to-day agenda is
        // generated rule-based from `availability` (7-day lead, 6-month horizon,
        // closed weekdays, blocked dates/slots) via the availability module.
        slots: Object.freeze(slots),
        availability: Object.freeze(availability),
        stripe: Object.freeze({
          successUrl: this.normalizePublicUrl(stripe.successUrl || ''),
          cancelUrl: this.normalizePublicUrl(stripe.cancelUrl || ''),
          requireBookingIntent: stripe.requireBookingIntent !== false,
          clientReferenceParameter: this.safeToken(
            stripe.clientReferenceParameter || 'client_reference_id',
            80
          ) || 'client_reference_id'
        }),
        analytics: Object.freeze({
          enabled: analytics.enabled !== false,
          debug: Boolean(analytics.debug),
          component: String(analytics.component || 'training_booking_v5'),
          events: Object.freeze({
            view: String(events.view || 'view_item_list'),
            selectPackage: String(events.selectPackage || 'select_item'),
            slotSelected: String(events.slotSelected || 'training_slot_selected'),
            slotStepCompleted: String(events.slotStepCompleted || 'training_slot_step_completed'),
            detailsCompleted: String(events.detailsCompleted || 'training_details_completed'),
            freeCheckSelected: String(events.freeCheckSelected || 'training_free_check_selected'),
            bookingCreated: String(events.bookingCreated || 'training_booking_created'),
            checkoutStarted: String(events.checkoutStarted || 'begin_checkout'),
            intakeSubmitted: String(events.intakeSubmitted || 'generate_lead')
          })
        }),
        packages: Object.freeze(packages)
      });
    }

    // Theme-managed availability. Each entry mirrors one `training_slot` section
    // block. No network call is ever made to build the agenda.
    parseConfigSlots(list) {
      if (!Array.isArray(list)) return [];
      const seen = new Set();
      const result = [];
      list.forEach((item, index) => {
        if (!isObject(item)) return;
        const date = String(item.date || '').trim();
        const startTime = String(item.startTime || '').trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime)) return;
        const endTime = /^([01]\d|2[0-3]):[0-5]\d$/.test(String(item.endTime || '').trim())
          ? String(item.endTime).trim()
          : '';
        const startsAt = this.parseDateValue(`${date}T${startTime}:00`);
        if (!startsAt) return;
        const endsAt = endTime ? this.parseDateValue(`${date}T${endTime}:00`) : null;

        const pkg = String(item.package || 'both').toLowerCase();
        // Start, Team and Actieplan book the SAME moments; a named package targets
        // one, any other/both value opens the slot to all three.
        const packages = pkg === 'start' || pkg === 'team' || pkg === 'action'
          ? [pkg]
          : ['start', 'team', 'action'];
        const delivery = String(item.delivery || 'both').toLowerCase();
        const deliveries = delivery === 'online' || delivery === 'on_location'
          ? [delivery]
          : ['online', 'on_location'];

        const id = this.safeReference(String(item.id || `slot_${index}`)) || `slot_${index}`;
        if (seen.has(id)) return;
        seen.add(id);

        result.push(Object.freeze({
          id,
          date,
          startTime,
          endTime,
          startsAt,
          endsAt,
          available: item.available !== false,
          packages,
          deliveries,
          label: '',
          timezone: 'Europe/Amsterdam'
        }));
      });
      return result.sort((a, b) => a.startsAt - b.startsAt);
    }

    // Rule-based availability config (booking window + closed weekdays + blocks).
    // The heavy lifting lives in the shared availability module; here we just keep
    // a normalised, frozen copy for day-status rendering and the generator call.
    parseAvailabilityConfig(raw) {
      const source = isObject(raw) ? raw : {};
      const list = (value) => String(value || '')
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter(Boolean);
      const leadDays = clamp(int(source.leadDays, 7), 0, 365);
      const horizonMonths = clamp(int(source.horizonMonths, 6), 1, 24);
      const closedWeekdays = list(source.closedWeekdaysRaw ?? source.closedWeekdays)
        .map((n) => int(n, -1))
        .filter((n) => n >= 0 && n <= 6);
      const times = (Array.isArray(source.times) ? source.times : ['10:00', '14:00'])
        .map((t) => String(t).trim())
        .filter((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t));
      const blockedDates = list(source.blockedDatesRaw ?? source.blockedDates)
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
      const blockedSlots = list(source.blockedSlotsRaw ?? source.blockedSlots)
        .map((s) => s.replace(/\s+/g, ' ').trim())
        .filter((s) => /^\d{4}-\d{2}-\d{2} ([01]\d|2[0-3]):[0-5]\d$/.test(s));
      return {
        leadDays,
        horizonMonths,
        closedWeekdays: closedWeekdays.length ? closedWeekdays : [0, 1, 6],
        times: times.length ? times : ['10:00', '14:00'],
        blockedDates,
        blockedSlots
      };
    }

    // Generate the full agenda from the rules + training_slot overrides, using the
    // shared availability module. Falls back to an empty agenda (never a crash)
    // when the module is unavailable, so the flow degrades to the intake route.
    generateAvailability() {
      const module = window.__IDENSCAN_AVAILABILITY__;
      if (!module || typeof module.buildAvailability !== 'function') {
        console.error('[IdenScan booking v5] Beschikbaarheidsmodule ontbreekt.');
        return [];
      }
      // The reserved duration is package-specific (Start 60, Team 90,
      // Actieplan 120), so each package's slots carry the correct end time and,
      // once the availability API honours it, the correct overlap window.
      const durationMinutes = this.state.package?.durationMinutes || undefined;
      return module.buildAvailability(
        { ...this.config.availability, durationMinutes, overrides: this.config.slots },
        new Date()
      );
    }

    computeBookingWindow() {
      const module = window.__IDENSCAN_AVAILABILITY__;
      if (module && typeof module.getBookingWindow === 'function') {
        return module.getBookingWindow(this.config.availability, new Date());
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const first = new Date(today);
      first.setDate(first.getDate() + (this.config.availability?.leadDays ?? 7));
      const last = new Date(today);
      last.setMonth(last.getMonth() + (this.config.availability?.horizonMonths ?? 6));
      last.setHours(23, 59, 59, 999);
      return { first, last };
    }

    // Human + machine status for a calendar cell: available / selected / closed
    // (e.g. Monday) / occupied (fully booked) / out-window (outside the window).
    dayStatus(date, iso) {
      if (this.availableDates && this.availableDates.has(iso)) return 'available';
      const window = this.bookingWindow;
      if (window) {
        const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const first = new Date(window.first.getFullYear(), window.first.getMonth(), window.first.getDate());
        if (day < first || day > window.last) return 'out-window';
      }
      const closed = new Set(this.config.availability?.closedWeekdays || []);
      if (closed.has(date.getDay())) return 'closed';
      return 'occupied';
    }

    setupBaseState() {
      this.steps.forEach((step) => {
        const active = int(step.dataset.step) === this.currentStep;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
        step.setAttribute('aria-hidden', String(!active));
        if ('inert' in step) step.inert = !active;
      });

      const toast = q(this.root, '[data-toast]');
      if (toast) toast.setAttribute('aria-atomic', 'true');

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(12, 0, 0, 0);
      const minDate = this.isoDate(tomorrow);
      ['preferred_date_1', 'preferred_date_2'].forEach((name) => {
        const field = this.field(name);
        if (field) field.min = minDate;
      });

      this.setText('[data-submit-label]', 'Bevestigen en betalen');
      this.toggleAll('[data-direct-payment-explainer]', true);
    }

    bindEvents() {
      const signal = this.lifecycle.signal;

      this.root.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const next = target.closest('[data-next-step]');
        const previous = target.closest('[data-prev-step]');
        const calendarPrevious = target.closest('[data-calendar-prev]');
        const calendarNext = target.closest('[data-calendar-next]');
        const calendarView = target.closest('[data-calendar-view]');
        const calendarDay = target.closest('[data-calendar-date]');
        const retry = target.closest('[data-calendar-retry]');
        const applyRecommendation = target.closest('[data-apply-recommendation]');
        const freeCheckLink = target.closest('[data-free-check-link]');
        const mobileAction = target.closest('[data-mobile-bar-action]');

        if (mobileAction && this.root.contains(mobileAction)) {
          event.preventDefault();
          this.triggerPrimaryAction();
          return;
        }

        // The confirm button is type="button" (never a native form submit) so
        // Shopify's contact-form captcha can never bypass our handler with a raw
        // form.submit(). The full backend POST + Stripe redirect runs here.
        const submitBtn = target.closest('[data-submit]');
        if (submitBtn && this.root.contains(submitBtn)) {
          event.preventDefault();
          void this.submit(event);
          return;
        }

        if (next && this.root.contains(next)) {
          event.preventDefault();
          if (this.submitLocked) return;
          const targetStep = int(next.dataset.nextStep);
          if (this.validateStep(this.currentStep)) {
            if (this.currentStep === 2) this.trackSlotStepCompleted();
            if (this.currentStep === 3) this.trackDetailsCompleted();
            this.goToStep(targetStep);
          }
          return;
        }

        if (previous && this.root.contains(previous)) {
          event.preventDefault();
          if (!this.submitLocked) this.goToStep(int(previous.dataset.prevStep));
          return;
        }

        if (calendarPrevious && this.root.contains(calendarPrevious)) {
          event.preventDefault();
          this.changeSlotGroup(-1);
          return;
        }

        if (calendarNext && this.root.contains(calendarNext)) {
          event.preventDefault();
          this.changeSlotGroup(1);
          return;
        }

        if (calendarView && this.root.contains(calendarView)) {
          event.preventDefault();
          this.setCalendarView(calendarView.dataset.calendarView);
          return;
        }

        if (calendarDay && this.root.contains(calendarDay)) {
          event.preventDefault();
          if (!calendarDay.disabled) this.selectCalendarDate(calendarDay.dataset.calendarDate);
          return;
        }

        if (retry && this.root.contains(retry)) {
          event.preventDefault();
          if (!this.submitLocked) void this.refreshRemoteAvailability({ reload: true });
          return;
        }

        if (applyRecommendation && this.root.contains(applyRecommendation)) {
          event.preventDefault();
          this.applyRecommendation({ announce: true });
          return;
        }

        if (freeCheckLink && this.root.contains(freeCheckLink)) {
          this.track(this.config.analytics.events.freeCheckSelected, {
            journey: 'free_team_risk_check',
            package_id: this.state.package?.id || 'not_selected'
          });
        }
      }, { signal });

      this.form.addEventListener('change', (event) => this.onChange(event), { signal });
      this.form.addEventListener('input', (event) => this.onInput(event), { signal });
      this.form.addEventListener('submit', (event) => void this.submit(event), { signal });

      // Restore the button after a bfcache back-navigation from Stripe, and
      // refresh the theme-native agenda so any expired moment drops out.
      window.addEventListener('pageshow', (event) => {
        this.setLoading(false);
        if (event.persisted && this.currentStep === 2 && this.usesCalendar()) {
          this.loadAvailability({ announce: false });
        }
      }, { signal });
    }

    onChange(event) {
      const field = event.target;
      if (!isField(field)) return;

      this.clearFieldError(field);

      // Pure step-3 intake answer: the selection is already shown by CSS and
      // nothing downstream depends on it. Skip the full recalc/rerender pipeline
      // so the guided intake stays instant on mobile (no availability recompute,
      // no summary rebuild, no full-form selection sweep on every tap).
      if (LIGHT_INTAKE_FIELDS.has(field.name)) return;

      if (field.name === 'package') {
        // Keep the chosen date/time across a package change. loadAvailability
        // re-validates the moment and only resets it (with a message) if it no
        // longer fits the new package.
        this.captureState();
        this.trackPackageSelection();
      }

      if (field.name === 'priorities') {
        this.enforcePriorityLimit(field);
      }

      if (field.name === 'slot_id') {
        this.captureSelectedSlot(field);
      }

      if (field.name === 'preferred_date_1') {
        this.updateSecondDateMinimum();
      }

      if (field.name === 'delivery_type') {
        this.applyLocationRules();
        if (this.currentStep === 2 && this.usesCalendar()) {
          // Re-filter the agenda; the chosen moment is preserved when it still fits.
          void this.loadAvailability({ announce: false });
        }
      }


      this.captureState();
      this.applyRoute();
      this.applyLocationRules();
      this.applyContactRules();
      this.updateSelections();
      this.updatePriorityCount();
      this.hideRelevantErrors(field.name);
      this.updateSummary();
      this.updateSubmitState();
      this.scheduleSafeDraftSave();
    }

    onInput(event) {
      const field = event.target;
      if (!isField(field)) return;

      this.clearFieldError(field);

      // Free-text intake answers ("Anders", trainingsdoel): read at submit time,
      // not shown in the summary. Skip the per-keystroke summary rebuild so the
      // text fields type without lag on mobile.
      if (LIGHT_INTAKE_FIELDS.has(field.name)) return;

      if (field.name === 'participants') {
        const previousRoute = this.state.route;
        this.captureState();
        this.applyRoute();
        if (previousRoute === 'direct' && this.state.route === 'intake') {
          this.toast(
            `Voor meer dan ${this.config.maxDirectParticipants} deelnemers stemmen we eerst groepsindeling en planning af.`,
            false
          );
        }
      }

      this.updateSummary();
      this.updateSubmitState();
      this.scheduleSafeDraftSave();
    }

    applyUrlDefaults() {
      const params = new URLSearchParams(window.location.search);
      const packageId = String(params.get('package') || params.get('pakket') || '').toLowerCase();
      if (packageId && this.config.packages[packageId]) {
        this.selectPackage(packageId, { track: false });
      }

      const token = this.safeToken(
        params.get('check_token') || params.get('token') || params.get('checkToken') || '',
        500
      );
      if (token) {
        const input = this.field('check_token');
        if (input) input.value = token;
      }
    }

    captureState() {
      const selected = q(this.form, 'input[name="package"]:checked');
      const packageId = selected?.value || '';
      this.state.package = packageId && this.config.packages[packageId]
        ? this.config.packages[packageId]
        : null;
      this.state.paymentMethod = this.value('payment_method');

      const participants = int(this.value('participants'));
      if (this.state.package?.route === 'intake') {
        this.state.route = 'intake';
        this.state.routeReason = 'package';
      } else if (participants > this.config.maxDirectParticipants) {
        this.state.route = 'intake';
        this.state.routeReason = 'participants';
      } else if (this.availabilityStatus === 'empty') {
        this.state.route = 'intake';
        this.state.routeReason = 'availability_empty';
      } else {
        this.state.route = 'direct';
        this.state.routeReason = 'direct';
      }

      // Scheduling surface is INDEPENDENT of route: all three packages pick a
      // moment on the shared calendar. We only fall back to the preferred-date
      // surface when a real calendar cannot be shown — a large group needs a
      // manual plan, or the agenda came back empty. Actieplan uses the exact
      // same direct calendar route as Start/Team.
      const pkg = this.state.package;
      const calendarCapable = Boolean(pkg) && pkg.usesCalendar !== false;
      const forcedPreferred = this.state.routeReason === 'participants' || this.availabilityStatus === 'empty';
      this.state.schedule = calendarCapable && !forcedPreferred ? 'calendar' : 'preferred';
    }

    // True when Step 2 shows the shared availability calendar (Start, Team AND
    // Actieplan). Used everywhere the moment picker is gated, decoupled from the
    // submit/payment `route`.
    usesCalendar() {
      return this.state.schedule === 'calendar';
    }

    invoiceAvailable() {
      return this.state.route === 'direct'
        && this.usesCalendar()
        && ['start', 'team'].includes(this.state.package?.id || '');
    }

    isInvoicePayment() {
      return this.requiresPayment() && this.state.paymentMethod === 'invoice';
    }

    isDepositPayment() {
      return this.requiresPayment() && this.state.paymentMethod === 'deposit';
    }

    applyPaymentRules() {
      const paymentRequired = this.requiresPayment();
      const invoiceAvailable = this.invoiceAvailable();
      const invoiceRadio = q(this.form, 'input[name="payment_method"][value="invoice"]');
      const paymentRadios = qa(this.form, 'input[name="payment_method"]');

      if (invoiceRadio) invoiceRadio.disabled = !paymentRequired || !invoiceAvailable;
      if (invoiceRadio?.checked && !invoiceAvailable) {
        invoiceRadio.checked = false;
        this.state.paymentMethod = '';
      }
      paymentRadios.forEach((field) => {
        if (field !== invoiceRadio) field.disabled = !paymentRequired;
        field.required = paymentRequired;
      });

      const invoiceSelected = paymentRequired && invoiceAvailable && this.state.paymentMethod === 'invoice';
      const depositSelected = paymentRequired && this.state.paymentMethod === 'deposit';
      if (invoiceSelected) {
        const invoiceCompany = this.field('invoiceCompanyName');
        const invoiceEmail = this.field('invoiceEmail');
        if (invoiceCompany && !invoiceCompany.value.trim()) invoiceCompany.value = this.value('company_name');
        if (invoiceEmail && !invoiceEmail.value.trim()) invoiceEmail.value = this.value('business_email');
      }
      this.toggleAll('[data-payment-choice]', paymentRequired);
      this.toggleAll('[data-invoice-unavailable]', paymentRequired && !invoiceAvailable);
      this.toggleAll('[data-invoice-fields]', invoiceSelected);
      this.toggleAll('[data-deposit-only]', depositSelected);
      this.toggleAll('[data-deposit-billing]', depositSelected);
      this.toggleAll('[data-direct-payment-explainer]', depositSelected);
      this.toggleAll('[data-summary-prices]', depositSelected);
      this.toggleAll('[data-summary-invoice]', invoiceSelected);
      this.toggleAll('[data-summary-intake]', !paymentRequired);

      const requiredInvoiceFields = new Set([
        'invoiceCompanyName', 'invoiceEmail', 'invoiceAddress',
        'invoicePostalCode', 'invoiceCity', 'invoiceCountry'
      ]);
      qa(this.form, '[data-invoice-fields] input').forEach((field) => {
        field.disabled = !invoiceSelected;
        field.required = invoiceSelected && requiredInvoiceFields.has(field.name);
        if (!invoiceSelected) this.clearFieldError(field);
      });
      qa(this.form, '[data-deposit-billing] input').forEach((field) => {
        field.disabled = !depositSelected;
      });
    }

    applyRoute({ announce = true } = {}) {
      const previousRoute = this.root.dataset.activeRoute || '';
      const calendar = this.usesCalendar();
      const pays = this.requiresPayment();
      this.root.dataset.activeRoute = this.state.route;

      // Scheduling surface follows the CALENDAR flag (shared live agenda for
      // Start, Team AND Actieplan) vs. the preferred-date fallback...
      this.toggleAll('[data-direct-schedule]', calendar);
      this.toggleAll('[data-intake-schedule]', !calendar);
      // ...but the payment surface follows whether a deposit is collected today;
      // direct Action now follows the same paid summary as Start/Team.
      this.toggleAll('[data-direct-consent]', pays);

      const terms = this.field('terms_confirmed');
      if (terms) {
        terms.required = pays;
        terms.disabled = !pays;
        if (!pays) terms.checked = false;
      }

      ['preferred_date_1', 'preferred_date_2', 'preferred_timeframe', 'intake_contact_preference']
        .forEach((name) => {
          const field = this.field(name);
          if (!field) return;
          field.disabled = calendar;
          field.required = !calendar && ['preferred_timeframe', 'intake_contact_preference'].includes(name);
        });

      if (!calendar) this.clearSelectedSlot();
      this.applyContactRules();
      this.applyPaymentRules();
      this.updateRouteCopy();
      this.updateRouteNote();
      this.updateSubmitState();

      if (announce && previousRoute && previousRoute !== this.state.route) {
        this.dispatch('route-change', {
          route: this.state.route,
          reason: this.state.routeReason,
          packageId: this.state.package?.id || null
        });
      }
    }

    updateRouteCopy() {
      const direct = this.state.route === 'direct';
      const calendar = this.usesCalendar();
      const pays = this.requiresPayment();
      // The schedule badge follows the calendar surface (shown for all three
      // packages), not the submit route.
      this.setText(
        '[data-schedule-badge]',
        calendar ? 'Beschikbare voorkeursmomenten' : 'Persoonlijke reactie binnen één werkdag'
      );
      // Small, calendar-only context for Actieplan's wider maatwerk window.
      this.toggleAll('[data-schedule-action-note]', calendar && this.state.package?.id === 'action');
      const paymentLabel = this.isInvoicePayment()
        ? 'Zakelijk op factuur'
        : (this.isDepositPayment() ? 'Online aanbetaling via Stripe' : 'Kies een betaalwijze');
      this.setText('[data-final-badge]', pays ? paymentLabel : 'Nog geen betaling');
      // Every direct package pays its configured Stripe deposit now. Generic
      // overflow/intake routes still submit without starting Checkout.
      const submitLabel = direct
        ? (this.isInvoicePayment() ? 'Boek op factuur' : (this.isDepositPayment() ? 'Naar veilige aanbetaling' : 'Kies een betaalwijze'))
        : 'Verstuur trainingsaanvraag';
      this.setText('[data-submit-label]', submitLabel);
      this.setText('[data-payment-note]', this.paymentNote());
    }

    updateRouteNote() {
      const note = q(this.root, '[data-route-note]');
      if (!note) return;

      const visible = this.state.route === 'intake';
      note.hidden = !visible;
      const cta = q(this.root, '[data-route-note-cta]');
      // The "voorstel op maat" CTA only appears for a large-group (26+) request.
      const showCta = visible && this.state.routeReason === 'participants';
      if (cta) cta.hidden = !showCta;
      if (!visible) return;

      if (this.state.routeReason === 'package') {
        this.setText('[data-route-note-title]', 'Voor het Actieplan plannen we eerst een korte intake');
        this.setText(
          '[data-route-note-text]',
          'We stemmen rollen, processen, afdelingen, planning en de definitieve prijs persoonlijk af. Je betaalt op deze pagina nog niets.'
        );
        return;
      }

      if (this.state.routeReason === 'participants') {
        this.setText(
          '[data-route-note-title]',
          `Vanaf ${this.config.maxDirectParticipants + 1} deelnemers maken we een voorstel op maat`
        );
        this.setText(
          '[data-route-note-text]',
          `Tot en met ${this.config.maxDirectParticipants} deelnemers kun je direct boeken. Voor een grotere groep stemmen we groepsindeling, planning en prijs eerst persoonlijk af.`
        );
        return;
      }

      this.setText('[data-route-note-title]', 'Er staat momenteel geen beschikbaar voorkeursmoment klaar');
      this.setText(
        '[data-route-note-text]',
        'Geef jullie voorkeursdata door. IdenScan controleert de agenda en reageert binnen één werkdag.'
      );
    }

    applyLocationRules() {
      const onLocation = this.value('delivery_type') === 'on_location';
      const fullAddress = q(this.root, '[data-full-address]');
      if (fullAddress) fullAddress.hidden = !onLocation;

      // Full training address, required only for on-location. When not on-location
      // the fields are disabled AND cleared, so a hidden field can never block the
      // submit and no stale address is ever sent along.
      ['location_street', 'location_postcode_final', 'location_city_final'].forEach((name) => {
        const field = this.field(name);
        if (!field) return;
        field.disabled = !onLocation;
        field.required = onLocation;
        if (!onLocation) { field.value = ''; this.clearFieldError(field); }
      });
    }

    applyContactRules() {
      // Phone is now a base required contact field for every route.
      const phone = this.field('phone');
      if (!phone) return;
      phone.required = true;
    }

    updateSecondDateMinimum() {
      const first = this.field('preferred_date_1');
      const second = this.field('preferred_date_2');
      if (!second) return;
      second.min = first?.value || second.min;
      if (first?.value && second.value && second.value < first.value) {
        second.value = '';
        this.toast('De tweede voorkeursdatum moet na de eerste datum liggen.', true);
      }
    }

    selectPackage(packageId, { track = true } = {}) {
      const input = q(this.form, `input[name="package"][value="${CSS.escape(packageId)}"]`);
      if (!input || input.disabled) return false;
      input.checked = true;
      this.captureState();
      this.applyRoute({ announce: false });
      this.updateSelections();
      this.updateSummary();
      this.updateSubmitState();
      if (track) this.trackPackageSelection();
      return true;
    }

    updateSelections() {
      qa(this.root, '[data-package-card]').forEach((card) => {
        card.classList.toggle('is-selected', Boolean(q(card, 'input[name="package"]:checked')));
      });
      qa(this.root, '.isb-v4__choice-card').forEach((card) => {
        card.classList.toggle('is-selected', Boolean(q(card, 'input:checked')));
      });
      qa(this.root, '.isb-v4__radio-card').forEach((card) => {
        card.classList.toggle('is-selected', Boolean(q(card, 'input:checked')));
      });
      qa(this.root, '.isb-v4__confirmation').forEach((card) => {
        card.classList.toggle('is-selected', Boolean(q(card, 'input:checked')));
      });
      qa(this.root, '.isb-v4__slot').forEach((card) => {
        card.classList.toggle('is-selected', Boolean(q(card, 'input[name="slot_id"]:checked')));
      });
    }

    enforcePriorityLimit(changedInput) {
      const selected = this.selectedValues('priorities');
      if (selected.length <= MAX_PRIORITIES) return;
      changedInput.checked = false;
      this.toast(`Kies maximaal ${MAX_PRIORITIES} punten voor extra nadruk.`, true);
    }

    updatePriorityCount() {
      const count = this.selectedValues('priorities').length;
      this.setText('[data-priority-count]', String(count));
      const error = q(this.root, '[data-priority-error]');
      if (error && count >= 1 && count <= MAX_PRIORITIES) error.hidden = true;
    }

    goToStep(stepNumber, { scroll = true, focus = true } = {}) {
      const target = this.steps.find((step) => int(step.dataset.step) === stepNumber);
      if (!target || stepNumber < 1 || stepNumber > 4) return;

      this.steps.forEach((step) => {
        const active = step === target;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
        step.setAttribute('aria-hidden', String(!active));
        if ('inert' in step) step.inert = !active;
      });

      this.currentStep = stepNumber;
      this.updateProgress();
      this.updateSummary();
      this.updateSubmitState();

      if (focus) q(target, 'h2')?.focus({ preventScroll: true });
      if (scroll) this.scrollTo(q(this.root, '[data-booking-app]') || target, 18);

      if (stepNumber === 2 && this.usesCalendar()) {
        // Returning to the Moment step re-fetches Neon so a slot taken in the
        // meantime disappears immediately (falls back to inline config on error).
        void this.refreshRemoteAvailability();
      }

      this.dispatch('step-change', {
        step: stepNumber,
        packageId: this.state.package?.id || null,
        route: this.state.route
      });
    }

    updateProgress() {
      qa(this.root, '[data-progress-item]').forEach((item) => {
        const number = int(item.dataset.progressItem);
        item.classList.toggle('is-active', number === this.currentStep);
        item.classList.toggle('is-complete', number < this.currentStep);
        if (number === this.currentStep) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });

      const bar = q(this.root, '[data-progress-bar]');
      if (bar) bar.style.width = `${((this.currentStep - 1) / 3) * 100}%`;
      this.updateMobileBar();
    }

    // Mobile-only sticky bar. Mirrors the primary action of the active step so a
    // small screen always shows package, moment and the next action without the
    // desktop summary column. CSS hides it above 980px.
    updateMobileBar() {
      const bar = q(this.root, '[data-mobile-bar]');
      if (!bar) return;
      bar.hidden = false;
      bar.setAttribute('aria-hidden', 'false');
      const pkg = this.state.package;
      let detail = 'Bekijk de drie pakketten';
      let action = 'Kies pakket';
      if (this.currentStep === 1) {
        detail = pkg ? 'Bekijk beschikbare momenten' : 'Bekijk de drie pakketten';
        action = pkg ? 'Naar moment' : 'Kies pakket';
      } else if (this.currentStep === 2) {
        detail = this.usesCalendar()
          ? (this.state.slot ? this.slotLabel(this.state.slot) : 'Kies een beschikbaar moment')
          : 'Geef jullie voorkeuren door';
        action = 'Naar voorbereiding';
      } else if (this.currentStep === 3) {
        detail = 'Korte voorbereiding invullen';
        action = 'Controleer boeking';
      } else {
        detail = this.isInvoicePayment()
          ? 'Officiële factuur per e-mail'
          : (this.isDepositPayment() && pkg?.estimatedCheckoutInclVat
            ? `Vandaag ${this.money(pkg.estimatedCheckoutInclVat)} incl. btw`
            : 'Rond de aanvraag af');
        action = this.isInvoicePayment()
          ? 'Boek op factuur'
          : (this.isDepositPayment() ? 'Naar aanbetaling' : (this.requiresPayment() ? 'Kies betaalwijze' : 'Verstuur aanvraag'));
      }
      this.setText('[data-mobile-bar-package]', pkg ? pkg.displayName : 'Kies een training');
      this.setText('[data-mobile-bar-detail]', detail);
      const actionBtn = q(this.root, '[data-mobile-bar-action]');
      if (actionBtn) {
        actionBtn.textContent = action;
        actionBtn.disabled = Boolean(this.submitLocked);
      }
    }

    // Proxy the mobile bar's button to the real advance/submit control of the
    // active step, so all existing validation and locking is reused.
    triggerPrimaryAction() {
      if (this.submitLocked) return;
      const activeStep = this.steps.find((step) => int(step.dataset.step) === this.currentStep);
      const control = activeStep ? q(activeStep, '[data-next-step], [data-submit]') : null;
      if (control instanceof HTMLElement) control.click();
    }

    validateStep(stepNumber) {
      this.clearErrors();
      this.captureState();
      this.applyRoute({ announce: false });
      this.applyLocationRules();
      this.applyContactRules();

      if (stepNumber === 1) {
        if (!this.state.package) {
          this.showInlineError('[data-package-error]');
          this.toast('Kies eerst een trainingspakket.', true);
          this.scrollTo(q(this.root, '[data-package-grid]'), 90);
          return false;
        }
        if (!this.state.package.ready) {
          this.toast('De prijsconfiguratie van dit pakket is niet geldig.', true);
          return false;
        }
        return true;
      }

      if (stepNumber === 2) {
        const invalid = this.firstInvalidInStep(2);
        if (invalid) return this.invalidate(invalid, this.messageFor(invalid));

        if (this.usesCalendar()) {
          const selected = this.findCurrentSlot();
          if (!selected?.available) {
            this.showInlineError('[data-slot-error]');
            this.toast('Kies eerst een beschikbaar tijdstip.', true);
            this.scrollTo(q(this.root, '[data-direct-schedule]'), 90);
            return false;
          }
        } else if (!this.validatePreferredDates()) {
          return false;
        }
        return true;
      }

      if (stepNumber === 3) {
        this.captureState();
        if (this.state.route === 'intake' && this.state.routeReason === 'participants') {
          this.toast('Door het aantal deelnemers moeten we eerst de planning afstemmen.', false);
          this.goToStep(2);
          return false;
        }

        const invalid = this.firstInvalidInStep(3);
        if (invalid) return this.invalidate(invalid, this.messageFor(invalid));

        const priorities = this.selectedValues('priorities').length;
        if (priorities > MAX_PRIORITIES) {
          this.showInlineError('[data-priority-error]');
          this.toast(`Kies maximaal ${MAX_PRIORITIES} punten voor extra nadruk.`, true);
          this.scrollTo(q(this.root, '[data-priorities]'), 100);
          return false;
        }
        return true;
      }

      return true;
    }

    validateFinal() {
      for (const step of [1, 2, 3]) {
        if (!this.validateStep(step)) {
          if (this.currentStep !== step) this.goToStep(step);
          return false;
        }
      }

      this.captureState();
      this.applyPaymentRules();

      const invalid = this.firstInvalidInStep(4);
      if (invalid) {
        this.goToStep(4);
        return this.invalidate(invalid, this.messageFor(invalid), true);
      }

      // On-location training needs a valid full address. Empty was already caught
      // (required); here we reject a malformed postcode calmly and write back the
      // normalised "1234 AB" value so the summary and e-mail stay clean.
      if (this.value('delivery_type') === 'on_location') {
        const postcodeField = this.field('location_postcode_final');
        const normalized = this.normalizePostcode(postcodeField?.value);
        if (postcodeField && !normalized) {
          this.goToStep(4);
          return this.invalidate(
            postcodeField,
            'Vul een geldige Nederlandse postcode in, bijvoorbeeld 1234 AB.',
            true
          );
        }
        if (postcodeField) postcodeField.value = normalized;
        const cityField = this.field('location_city_final');
        if (cityField) cityField.value = this.cleanAddressPart(cityField.value, 100);
        const streetField = this.field('location_street');
        if (streetField) streetField.value = this.cleanAddressPart(streetField.value, 160);
      }

      if (this.requiresPayment() && !this.field('terms_confirmed')?.checked) {
        this.goToStep(4);
        return this.invalidate(
          this.field('terms_confirmed'),
          'Ga akkoord met de voorwaarden en het annuleringsbeleid.',
          true
        );
      }

      if (this.value('company_fax')) {
        this.finalError('De aanvraag kon niet worden gecontroleerd. Vernieuw de pagina en probeer opnieuw.');
        return false;
      }

      return true;
    }

    validatePreferredDates() {
      const first = this.value('preferred_date_1');
      const second = this.value('preferred_date_2');
      if (first && second && second < first) {
        return this.invalidate(
          this.field('preferred_date_2'),
          'De tweede voorkeursdatum moet na de eerste datum liggen.'
        );
      }
      return true;
    }

    firstInvalidInStep(stepNumber) {
      const step = this.steps.find((candidate) => int(candidate.dataset.step) === stepNumber);
      if (!step) return null;
      return qa(step, 'input, select, textarea').find((field) => (
        !field.disabled && !field.checkValidity()
      )) || null;
    }

    clearErrors() {
      qa(this.form, '.is-invalid, [aria-invalid="true"]').forEach((field) => this.clearFieldError(field));
      qa(
        this.root,
        '[data-package-error], [data-slot-error], [data-department-error], [data-priority-error], [data-error-summary]'
      ).forEach((node) => { node.hidden = true; });
    }

    clearFieldError(field) {
      if (!field) return;
      field.classList.remove('is-invalid');
      field.removeAttribute('aria-invalid');
      const host = field.closest('label, fieldset') || field.parentElement;
      const message = host ? host.querySelector('[data-field-error]') : null;
      if (message) message.hidden = true;
    }

    // Premium inline field error, shown directly under the field and wired to the
    // control via aria-describedby (no innerHTML, no personal data logged).
    setFieldMessage(field, message) {
      const host = field.closest('label, fieldset') || field.parentElement;
      if (!host) return;
      let node = host.querySelector('[data-field-error]');
      if (!node) {
        node = document.createElement('p');
        node.className = 'isb-v4__field-error';
        node.setAttribute('data-field-error', '');
        node.setAttribute('role', 'alert');
        node.id = `${field.name || 'veld'}-error-${this.createId('fe')}`;
        host.appendChild(node);
        field.setAttribute('aria-describedby', node.id);
      }
      node.textContent = message || 'Controleer dit veld.';
      node.hidden = false;
    }

    hideRelevantErrors(name) {
      if (name === 'package') this.hide('[data-package-error]');
      if (name === 'slot_id') this.hide('[data-slot-error]');
      if (name === 'departments') this.hide('[data-department-error]');
      if (name === 'priorities') this.hide('[data-priority-error]');
    }

    showInlineError(selector, message) {
      const node = q(this.root, selector);
      if (!node) return;
      if (typeof message === 'string' && message) node.textContent = message;
      node.hidden = false;
    }

    invalidate(field, message, final = false) {
      if (!field) return false;
      field.classList.add('is-invalid');
      field.setAttribute('aria-invalid', 'true');
      this.setFieldMessage(field, message);
      const target = field.closest('label, fieldset, details') || field;
      try {
        field.focus({ preventScroll: true });
      } catch (_) {
        // Ignore focus errors from hidden browser-native controls.
      }
      this.scrollTo(target, 110);
      if (final) this.finalError(message);
      else this.toast(message, true);
      return false;
    }

    finalError(message, title = 'Controleer je gegevens') {
      const box = q(this.root, '[data-error-summary]');
      this.setText('[data-error-title]', title);
      this.setText('[data-error-message]', message);
      if (box) {
        box.hidden = false;
        box.focus({ preventScroll: true });
        this.scrollTo(box, 100);
      }
      this.dispatch('error', { code: 'validation', message });
      return false;
    }

    messageFor(field) {
      const label = field.closest('label')?.querySelector(':scope > span')?.textContent
        ?.replace('*', '').trim()
        || field.closest('fieldset')?.querySelector('legend')?.textContent?.replace('*', '').trim()
        || field.name
        || 'dit veld';

      if (field.validity.valueMissing) {
        if (field.name === 'phone' && this.value('intake_contact_preference') === 'phone') {
          return 'Vul een telefoonnummer in voor telefonisch contact.';
        }
        return `Vul “${label}” in.`;
      }
      if (field.validity.typeMismatch) return 'Vul een geldig zakelijk e-mailadres in.';
      if (field.validity.rangeUnderflow) return `Vul bij “${label}” minimaal ${field.min} in.`;
      if (field.validity.rangeOverflow) return `Vul bij “${label}” maximaal ${field.max} in.`;
      if (field.validity.patternMismatch) return `Controleer de invoer bij “${label}”.`;
      return field.validationMessage || 'Controleer dit veld.';
    }

    async loadCheckContext() {
      const token = this.value('check_token');
      if (!token) {
        this.setContextState('none');
        return null;
      }
      if (!this.config.api.context) {
        this.setContextState('invalid');
        return null;
      }
      if (this.contextPromise) return this.contextPromise;

      this.contextPromise = (async () => {
        try {
          const url = new URL(this.config.api.context);
          url.searchParams.set('token', token);
          const payload = await this.requestJSON(url.toString(), {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          });
          const context = this.normalizeContext(payload);
          this.state.context = context;
          this.applyContext(context);
          return context;
        } catch (error) {
          if (this.isLifecycleAbort(error)) return null;
          console.error('[IdenScan booking v5] Checkcontext laden mislukt.', error);
          this.setContextState(error?.details?.status === 404 || error?.details?.status === 410 ? 'invalid' : 'invalid');
          return null;
        } finally {
          this.contextPromise = null;
        }
      })();

      return this.contextPromise;
    }

    normalizeContext(payload) {
      const source = payload?.context || payload?.data || payload || {};
      const recommendation = source.recommendation || payload?.recommendation || {};
      const statusRaw = String(source.status || payload?.status || '').toLowerCase();
      const status = ['completed', 'ready', 'finished'].includes(statusRaw)
        ? 'completed'
        : ['pending', 'submitted', 'under_review', 'in_review', 'processing'].includes(statusRaw)
          ? 'pending'
          : 'invalid';

      return {
        status,
        reference: this.clean(
          source.reference || source.checkReference || payload?.reference || '',
          100
        ),
        bookingIntentId: this.safeReference(
          source.bookingIntentId || source.booking_intent_id || payload?.bookingIntentId || ''
        ),
        companyName: this.clean(
          source.companyName || source.company_name || source.company || '',
          120
        ),
        summary: this.clean(
          recommendation.summary || source.summary || source.reportSummary || '',
          500
        ),
        packageId: this.clean(
          recommendation.packageId || recommendation.package_id || source.recommendedPackage || '',
          30
        ).toLowerCase(),
        topics: Array.isArray(recommendation.topics)
          ? recommendation.topics.map((topic) => this.clean(topic, 120)).filter(Boolean)
          : Array.isArray(source.recommendedTopics)
            ? source.recommendedTopics.map((topic) => this.clean(topic, 120)).filter(Boolean)
            : [],
        prefill: isObject(source.prefill) ? source.prefill : {}
      };
    }

    applyContext(context) {
      if (!context || context.status === 'invalid') {
        this.setContextState('invalid');
        return;
      }

      this.setContextState(context.status);
      if (context.reference) {
        const field = this.field('check_reference');
        if (field) field.value = context.reference;
      }
      if (context.bookingIntentId) {
        const field = this.field('booking_intent_id');
        if (field) field.value = context.bookingIntentId;
        this.state.bookingIntentId = context.bookingIntentId;
      }
      if (context.companyName) this.setText('[data-context-company]', context.companyName);
      if (context.summary) this.setText('[data-context-summary]', context.summary);
      if (context.packageId && this.config.packages[context.packageId]) {
        const field = this.field('recommended_package');
        if (field) field.value = context.packageId;
      }

      this.prefillFromContext(context.prefill);
      if (context.status === 'completed') {
        this.applyRecommendation({ announce: false });
        this.selectRadioValue('check_status', 'completed');
      } else if (context.status === 'pending') {
        this.selectRadioValue('check_status', 'pending');
      }
      this.updateSelections();
      this.updateSummary();
    }

    setContextState(state) {
      const container = q(this.root, '[data-check-context]');
      if (!container) return;
      container.dataset.contextState = state;
      ['none', 'pending', 'completed', 'invalid'].forEach((name) => {
        const node = q(container, `[data-context-${name}]`);
        if (node) node.hidden = name !== state;
      });
    }

    prefillFromContext(prefill) {
      if (!isObject(prefill)) return;
      const map = {
        companyName: 'company_name',
        company_name: 'company_name',
        website: 'website',
        contactName: 'contact_name',
        contact_name: 'contact_name',
        businessEmail: 'business_email',
        business_email: 'business_email',
        phone: 'phone',
        employeeCount: 'employee_count',
        employee_count: 'employee_count',
        companyType: 'company_type',
        company_type: 'company_type'
      };
      Object.entries(map).forEach(([sourceKey, fieldName]) => {
        const value = prefill[sourceKey];
        const field = this.field(fieldName);
        if (!field || !value || field.value) return;
        field.value = this.clean(value, field.maxLength > 0 ? field.maxLength : 200);
      });
    }

    applyRecommendation({ announce = true } = {}) {
      const context = this.state.context;
      if (!context || context.status !== 'completed') return;

      if (context.packageId && this.config.packages[context.packageId]) {
        this.selectPackage(context.packageId, { track: false });
      }

      const desired = new Set(context.topics.map((topic) => this.topicKey(topic)));
      if (desired.size) {
        qa(this.form, 'input[name="priorities"]').forEach((input) => {
          input.checked = desired.has(this.topicKey(input.value));
        });
      }

      this.updateSelections();
      this.updatePriorityCount();
      this.updateSummary();
      this.scheduleSafeDraftSave();
      if (announce) this.toast('De aanbeveling uit jullie Team Risico Check is toegepast.');
    }

    topicKey(value) {
      const normalized = String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

      const aliases = [
        ['phishing', 'phishing'],
        ['factuur', 'factuurfraude'],
        ['iban', 'factuurfraude'],
        ['ceo', 'ceo fraude'],
        ['qr', 'verdachte links'],
        ['link', 'verdachte links'],
        ['klant', 'klant personeelsgegevens'],
        ['personeel', 'klant personeelsgegevens'],
        ['ai', 'veilig gebruik ai'],
        ['wachtwoord', 'accounts wachtwoorden mfa'],
        ['mfa', 'accounts wachtwoorden mfa'],
        ['datalek', 'datalekken intern melden'],
        ['leverancier', 'leveranciers klantenfraude'],
        ['scenario', 'eigen scenarios']
      ];
      const alias = aliases.find(([needle]) => normalized.includes(needle));
      return alias ? alias[1] : normalized;
    }

    // Fetch the optional remote availability set (GET /api/availability). When it
    // returns a usable response its keys refine the locally generated agenda (e.g.
    // hiding moments that are held or paid). Any missing-config/request/schema
    // failure is non-fatal: the local agenda stays visible and bookable.
    async refreshRemoteAvailability({ reload = true } = {}) {
      const base = this.config.api.availability;
      if (!base) {
        console.error('[IdenScan booking v5] availability API unavailable: missing_config');
        this.remoteAvailability = { status: 'error', ok: false, keys: null, at: Date.now(), error: 'missing_config' };
        if (reload && this.usesCalendar() && this.state.package) this.loadAvailability({ announce: false });
        return false;
      }
      // Pass the selected package + its reserved duration so the availability API
      // can exclude start-times whose window would overlap an existing booking
      // (Start 60, Team 90, Actieplan 120). A backend that ignores these params
      // simply returns the same free set as before — safe, additive contract.
      const pkg = this.state.package;
      let url = base;
      if (pkg) {
        const sep = base.includes('?') ? '&' : '?';
        url = `${base}${sep}package=${encodeURIComponent(pkg.id)}&duration=${encodeURIComponent(pkg.durationMinutes || 0)}`;
      }
      try {
        const data = await this.requestJSON(url, { method: 'GET', headers: { Accept: 'application/json' } }, 12_000);
        if (!isObject(data) || !Array.isArray(data.slots)) {
          throw new BookingError('availability_schema_invalid', 'De beschikbaarheidsresponse is ongeldig.');
        }
        const list = data.slots;
        const keys = new Set();
        for (const slot of list) {
          const date = String(slot?.date || '');
          const time = String(slot?.startTime || '');
          const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T00:00:00Z`) : null;
          const validDate = parsedDate
            && !Number.isNaN(parsedDate.getTime())
            && parsedDate.toISOString().slice(0, 10) === date;
          if (!isObject(slot) || !validDate || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
            throw new BookingError('availability_schema_invalid', 'De beschikbaarheidsresponse bevat een ongeldig moment.');
          }
          keys.add(`${date} ${time}`);
        }
        this.remoteAvailability = { status: 'success', ok: true, keys, at: Date.now() };
      } catch (error) {
        // Never surface technical details or silently expose locally generated slots.
        const code = error?.code || 'unknown';
        console.error('[IdenScan booking v5] availability API unavailable:', code);
        this.remoteAvailability = { status: 'error', ok: false, keys: null, at: Date.now(), error: code };
      }
      if (reload && this.usesCalendar() && this.state.package) this.loadAvailability({ announce: false });
      return this.remoteAvailability.status === 'success';
    }

    loadAvailability({ announce = false } = {}) {
      this.captureState();
      if (!this.usesCalendar() || !this.state.package) {
        return [];
      }

      const now = Date.now();
      const delivery = this.value('delivery_type');
      const selectedId = this.state.slot?.id || '';

      // The frontend generates the rule-based agenda locally and it is always
      // visible/bookable. When the remote availability service returns a usable
      // set of allowed date/time keys we additionally filter against it, but a
      // missing/failing/invalid remote response never removes the local agenda.
      this.bookingWindow = this.computeBookingWindow();
      const remote = this.remoteAvailability;
      const remoteUsable = remote?.status === 'success' && remote.ok && remote.keys instanceof Set;
      this.slots = this.generateAvailability()
        .filter((slot) => {
          if (slot.startsAt.getTime() <= now) return false;
          if (!slot.available) return false;
          if (!slot.packages.includes(this.state.package.id)) return false;
          if ((delivery === 'online' || delivery === 'on_location') && !slot.deliveries.includes(delivery)) {
            return false;
          }
          // Only apply the remote set as an extra filter when the service returned
          // a usable response; otherwise the locally generated agenda stands.
          if (remoteUsable && !remote.keys.has(`${slot.date} ${slot.startTime}`)) return false;
          return true;
        })
        .map((slot) => ({ ...slot }));

      this.availableDates = new Set(this.slots.map((slot) => slot.date));
      this.lastAvailabilityAt = now;
      this.availabilityStatus = this.slots.some((slot) => slot.available) ? 'ready' : 'empty';

      if (selectedId) {
        const selected = this.slots.find((slot) => slot.id === selectedId && slot.available);
        this.state.slot = selected || null;
        // The chosen moment no longer fits (e.g. after a delivery/package change):
        // reset it deliberately and tell the visitor, instead of silently keeping it.
        if (!selected) {
          this.clearSelectedSlot();
          this.selectedDate = '';
          this.toast(
            'Het eerder gekozen moment past niet meer bij deze keuze. Kies een nieuw moment.',
            true
          );
        }
      }

      this.buildSlotGroups();
      this.renderAvailability();
      this.captureState();
      this.applyRoute();
      this.updateSummary();
      this.updateSubmitState();
      if (announce && this.availabilityStatus === 'ready') this.toast('De agenda is bijgewerkt.');
      return this.slots;
    }

    parseDateValue(value) {
      if (!value) return null;
      if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
      if (typeof value === 'number') {
        const date = new Date(value < 10_000_000_000 ? value * 1000 : value);
        return Number.isNaN(date.getTime()) ? null : date;
      }
      const date = new Date(String(value));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    buildSlotGroups() {
      const groups = new Map();
      this.slots.forEach((slot) => {
        const month = new Date(slot.startsAt.getFullYear(), slot.startsAt.getMonth(), 1, 12);
        const key = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
        if (!groups.has(key)) groups.set(key, { key, month, slots: [] });
        groups.get(key).slots.push(slot);
      });
      this.slotGroups = Array.from(groups.values()).sort((a, b) => a.month - b.month);
      const selectedIndex = this.state.slot
        ? this.slotGroups.findIndex((group) => group.slots.some((slot) => slot.id === this.state.slot.id))
        : -1;
      this.slotGroupIndex = selectedIndex >= 0 ? selectedIndex : 0;
      const selectedDate = this.state.slot?.startsAt || null;
      const firstDate = this.slotGroups[0]?.slots[0]?.startsAt || null;
      if (!this.calendarCursor || !this.slots.some((slot) => this.sameMonth(slot.startsAt, this.calendarCursor))) {
        this.calendarCursor = selectedDate || firstDate;
      }
      if (this.state.slot) this.selectedDate = this.state.slot.date;
    }

    renderAvailability() {
      const loading = q(this.root, '[data-slot-loading]');
      const shell = q(this.root, '[data-calendar-shell]');
      const empty = q(this.root, '[data-slot-empty]');
      const calendar = q(this.root, '[data-slot-calendar]');
      if (!shell || !empty || !calendar) return;

      if (loading) loading.hidden = true;
      calendar.replaceChildren();

      const emptyTitle = q(empty, 'strong');
      const emptyText = q(empty, 'p');
      if (!this.slotGroups.length || this.availabilityStatus === 'empty') {
        shell.hidden = true;
        empty.hidden = false;
        if (emptyTitle) emptyTitle.textContent = 'Geen beschikbaar voorkeursmoment gevonden';
        if (emptyText) emptyText.textContent = 'Geef jullie voorkeuren door. IdenScan controleert de agenda en reageert meestal binnen één werkdag.';
        this.setText('[data-slots-count]', 'Geen beschikbaar voorkeursmoment');
        this.captureState();
        this.applyRoute();
        return;
      }

      this.calendarCursor = this.calendarCursor || this.slots[0].startsAt;
      shell.hidden = false;
      empty.hidden = true;
      this.setText('[data-calendar-period]', this.calendarPeriodLabel());
      this.setText(
        '[data-slots-count]',
        `${this.slots.filter((slot) => slot.available).length} beschikbaar${this.slots.filter((slot) => slot.available).length === 1 ? '' : 'e'} moment${this.slots.filter((slot) => slot.available).length === 1 ? '' : 'en'}`
      );

      const previous = q(this.root, '[data-calendar-prev]');
      const next = q(this.root, '[data-calendar-next]');
      const bounds = this.calendarBounds();
      if (previous) previous.disabled = !bounds.canPrevious;
      if (next) next.disabled = !bounds.canNext;

      this.renderCalendarDays(calendar);
      this.renderTimes();
      qa(this.root, '[data-calendar-view]').forEach((button) => {
        const active = button.dataset.calendarView === this.calendarView;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.updateSelections();
      this.updateSelectedNote();
    }

    renderCalendarDays(calendar) {
      const cursor = new Date(this.calendarCursor);
      let start;
      let count;
      if (this.calendarView === 'week') {
        start = this.mondayFor(cursor);
        count = 7;
      } else {
        const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1, 12);
        start = this.mondayFor(first);
        count = 42;
      }

      const statusText = {
        available: 'beschikbaar',
        closed: 'gesloten',
        occupied: 'volgeboekt',
        'out-window': 'buiten boekingsvenster'
      };

      for (let index = 0; index < count; index += 1) {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        const iso = this.isoDate(date);
        const dateSlots = this.slots.filter((slot) => slot.date === iso);
        const outside = this.calendarView === 'month' && !this.sameMonth(date, cursor);
        const status = this.dayStatus(date, iso);
        const available = status === 'available';
        const selected = iso === this.selectedDate;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'isb-v4__calendar-day';
        button.dataset.calendarDate = iso;
        button.dataset.dayStatus = status;
        // Status is conveyed by class, data-attribute AND text/aria — never by
        // colour alone — so it is clear for everyone.
        button.classList.toggle('is-outside', outside);
        button.classList.toggle('is-available', available && !outside);
        button.classList.toggle('is-closed', status === 'closed' && !outside);
        button.classList.toggle('is-occupied', status === 'occupied' && !outside);
        button.classList.toggle('is-out-window', status === 'out-window' && !outside);
        button.classList.toggle('is-selected', selected && available);
        button.disabled = outside || !available;
        button.setAttribute('aria-pressed', String(selected && available));
        button.setAttribute('aria-label', available
          ? `${this.dateLabel(date)}, ${dateSlots.length} beschikbare ${dateSlots.length === 1 ? 'tijd' : 'tijden'}`
          : `${this.dateLabel(date)}, ${statusText[status] || 'niet beschikbaar'}`);

        const weekday = document.createElement('em');
        weekday.textContent = new Intl.DateTimeFormat('nl-NL', { weekday: 'short' }).format(date).replace('.', '');
        const number = document.createElement('strong');
        number.textContent = String(date.getDate());
        const meta = document.createElement('span');
        meta.textContent = available
          ? `${dateSlots.length} ${dateSlots.length === 1 ? 'tijd' : 'tijden'}`
          : (statusText[status] || '');
        button.append(weekday, number, meta);
        calendar.appendChild(button);
      }
    }

    renderTimes() {
      const panel = q(this.root, '[data-slot-times]');
      const list = q(this.root, '[data-slot-time-list]');
      if (!panel || !list) return;
      list.replaceChildren();
      const slots = this.selectedDate
        ? this.slots.filter((slot) => slot.date === this.selectedDate)
        : [];
      panel.hidden = slots.length === 0;
      if (!slots.length) return;
      const date = slots[0].startsAt;
      this.setText('[data-times-heading]', `Kies een tijd op ${this.dateLabel(date)}`);
      slots.forEach((slot) => list.appendChild(this.renderSlot(slot)));
    }

    renderSlot(slot) {
      const label = document.createElement('label');
      label.className = `isb-v4__slot${this.state.slot?.id === slot.id ? ' is-selected' : ''}`;
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'slot_id';
      input.value = slot.id;
      input.checked = this.state.slot?.id === slot.id;
      input.disabled = !slot.available || !this.usesCalendar();
      input.dataset.available = String(slot.available);
      input.dataset.slotId = slot.id;
      input.dataset.startsAt = slot.startsAt.toISOString();
      input.dataset.endsAt = slot.endsAt?.toISOString() || '';
      input.dataset.slotLabel = this.slotLabel(slot);
      input.setAttribute('aria-label', `${this.slotLabel(slot)} — beschikbaar`);

      const title = document.createElement('span');
      title.textContent = slot.endTime ? `${slot.startTime} – ${slot.endTime}` : slot.startTime;
      const detail = document.createElement('small');
      detail.textContent = 'uur';
      label.append(input, title, detail);
      return label;
    }

    selectCalendarDate(date) {
      if (!this.slots.some((slot) => slot.date === date)) return;
      if (this.state.slot?.date !== date) this.clearSelectedSlot();
      this.selectedDate = date;
      const parsed = this.parseDateValue(`${date}T12:00:00`);
      if (parsed) this.calendarCursor = parsed;
      this.renderAvailability();
      q(this.root, '[data-slot-times]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    setCalendarView(view) {
      if (!['month', 'week'].includes(view) || view === this.calendarView) return;
      this.calendarView = view;
      if (view === 'week') {
        const anchor = this.selectedDate
          ? this.parseDateValue(`${this.selectedDate}T12:00:00`)
          : this.slots[0]?.startsAt;
        if (anchor) this.calendarCursor = anchor;
      }
      this.renderAvailability();
    }

    captureSelectedSlot(input) {
      if (!input.checked) return;
      const slot = this.slots.find((candidate) => candidate.id === input.value);
      if (!slot?.available) return;
      this.state.slot = slot;
      this.selectedDate = slot.date;
      this.updateSelections();
      this.updateSelectedNote();
      this.updateSummary();
      this.track(this.config.analytics.events.slotSelected, {
        item_id: this.state.package?.id || '',
        slot_id: slot.id,
        slot_start: slot.startsAt.toISOString(),
        delivery_type: this.value('delivery_type') || ''
      });
      this.dispatch('slot-selected', {
        slotId: slot.id,
        startsAt: slot.startsAt.toISOString()
      });
    }

    clearSelectedSlot() {
      this.state.slot = null;
      qa(this.form, 'input[name="slot_id"]').forEach((input) => { input.checked = false; });
      this.updateSelections();
      this.updateSelectedNote();
    }

    // Confirmation copy for the chosen moment, including the personal-confirmation
    // promise required by the booking flow.
    updateSelectedNote() {
      const note = q(this.root, '[data-selected-note]');
      if (!note) return;
      const visible = this.usesCalendar() && Boolean(this.state.slot);
      note.hidden = !visible;
      if (visible) this.setText('[data-selected-note-label]', this.slotLabel(this.state.slot));
    }

    resetAvailability() {
      this.availabilityStatus = 'idle';
      this.slots = [];
      this.slotGroups = [];
      this.slotGroupIndex = 0;
      this.calendarCursor = null;
      this.selectedDate = '';
      this.clearSelectedSlot();
      const calendar = q(this.root, '[data-slot-calendar]');
      if (calendar) calendar.replaceChildren();
      const shell = q(this.root, '[data-calendar-shell]');
      const empty = q(this.root, '[data-slot-empty]');
      if (shell) shell.hidden = true;
      if (empty) empty.hidden = true;
      this.setText('[data-slots-count]', 'Agenda wordt geladen');
    }

    findCurrentSlot() {
      if (!this.state.slot?.id) return null;
      return this.slots.find((slot) => slot.id === this.state.slot.id) || null;
    }

    changeSlotGroup(direction) {
      if (!this.calendarCursor || !direction) return;
      const target = new Date(this.calendarCursor);
      if (this.calendarView === 'week') target.setDate(target.getDate() + (direction * 7));
      else target.setMonth(target.getMonth() + direction, 1);
      const bounds = this.calendarBounds(target);
      if (!bounds.targetAllowed) return;
      this.calendarCursor = target;
      this.renderAvailability();
    }

    calendarBounds(target = this.calendarCursor) {
      const first = this.slots[0]?.startsAt;
      const last = this.slots[this.slots.length - 1]?.startsAt;
      if (!first || !last || !target) return { canPrevious: false, canNext: false, targetAllowed: false };
      const periodStart = (date) => this.calendarView === 'week'
        ? this.mondayFor(date)
        : new Date(date.getFullYear(), date.getMonth(), 1, 12);
      const current = periodStart(this.calendarCursor);
      const candidate = periodStart(target);
      const minimum = periodStart(first);
      const maximum = periodStart(last);
      const step = this.calendarView === 'week' ? 7 : 1;
      const before = new Date(current);
      const after = new Date(current);
      if (this.calendarView === 'week') {
        before.setDate(before.getDate() - step);
        after.setDate(after.getDate() + step);
      } else {
        before.setMonth(before.getMonth() - step);
        after.setMonth(after.getMonth() + step);
      }
      return {
        canPrevious: before >= minimum,
        canNext: after <= maximum,
        targetAllowed: candidate >= minimum && candidate <= maximum
      };
    }

    calendarPeriodLabel() {
      if (this.calendarView === 'week') return this.weekRange(this.mondayFor(this.calendarCursor));
      return new Intl.DateTimeFormat('nl-NL', { month: 'long', year: 'numeric' }).format(this.calendarCursor);
    }

    sameMonth(a, b) {
      return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
    }

    dateLabel(date) {
      return new Intl.DateTimeFormat('nl-NL', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      }).format(date);
    }

    slotLabel(slot) {
      return new Intl.DateTimeFormat('nl-NL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(slot.startsAt);
    }

    slotDetail(slot) {
      if (slot.label) return slot.label;
      if (slot.endsAt) {
        const end = new Intl.DateTimeFormat('nl-NL', {
          hour: '2-digit',
          minute: '2-digit'
        }).format(slot.endsAt);
        return `Tot ${end} uur`;
      }
      const duration = this.state.package?.durationMinutes || 0;
      return duration ? `${duration} minuten` : 'Beschikbaar';
    }

    mondayFor(date) {
      const monday = new Date(date);
      const day = monday.getDay();
      monday.setDate(monday.getDate() - (day === 0 ? 6 : day - 1));
      monday.setHours(12, 0, 0, 0);
      return monday;
    }

    weekRange(monday) {
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      const start = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' }).format(monday);
      const end = new Intl.DateTimeFormat('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).format(sunday);
      return `${start} – ${end}`;
    }

    isoDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    updateSummary() {
      this.captureState();
      const pkg = this.state.package;
      const empty = q(this.root, '[data-summary-empty]');
      const content = q(this.root, '[data-summary-content]');
      if (empty) empty.hidden = Boolean(pkg);
      if (content) content.hidden = !pkg;

      if (!pkg) {
        this.setText('[data-summary-status]', 'Nog niet compleet');
        return;
      }

      const company = this.value('company_name') || 'Nog invullen';
      const companyMeta = [this.companyTypeLabel(), this.value('website')].filter(Boolean).join(' · ') || 'Zakelijke boeking';
      const participants = this.value('participants');
      const employeeCount = this.field('employee_count')?.selectedOptions?.[0]?.textContent || 'Nog invullen';
      const delivery = this.deliveryLabel();
      // Any calendar-scheduled package (incl. Actieplan) shows the chosen moment;
      // only the preferred-date fallback uses the intake label.
      const moment = this.usesCalendar()
        ? (this.state.slot ? this.slotLabel(this.state.slot) : 'Nog kiezen')
        : this.intakeMomentLabel();
      const priorities = this.selectedValues('priorities');
      const priorityLabels = priorities.map((value) => this.selectedChoiceLabel('priorities', value));

      this.setText(
        '[data-summary-status]',
        this.state.route === 'direct' ? 'Voorkeursmoment gekozen' : 'Persoonlijke bevestiging'
      );
      this.setText('[data-summary-company]', company);
      this.setText('[data-summary-company-meta]', companyMeta);
      this.setText('[data-summary-employees]', employeeCount);
      this.setText('[data-summary-package]', pkg.displayName);
      this.setText(
        '[data-summary-participants]',
        participants ? `${participants} deelnemer${int(participants) === 1 ? '' : 's'}` : 'Nog invullen'
      );
      this.setText('[data-summary-delivery]', delivery);
      this.setText('[data-summary-moment]', moment);
      this.setText('[data-summary-check]', this.checkStatusLabel());
      const depositVat = Math.max(0, int(pkg.estimatedCheckoutInclVat) - int(pkg.depositExVat));
      this.setText('[data-summary-total]', this.money(pkg.totalExVat));
      this.setText('[data-summary-deposit]', this.money(pkg.depositExVat));
      this.setText('[data-summary-vat]', this.money(depositVat));
      this.setText('[data-summary-checkout]', pkg.estimatedCheckoutInclVat ? this.money(pkg.estimatedCheckoutInclVat) : 'Nog geen betaling');
      this.setText('[data-summary-remaining]', this.money(pkg.remainingExVat));

      // Step-4 amounts card (also visible on mobile where the aside is hidden).
      const pays = this.requiresPayment();
      const depositSelected = this.isDepositPayment();
      this.toggleAll('[data-review-amounts-pay]', depositSelected);
      this.toggleAll('[data-review-amounts-intake]', !pays);
      this.setText('[data-review-amounts-badge]', depositSelected ? 'Veilige aanbetaling' : 'Nog geen betaling');
      this.setText('[data-review-total]', this.money(pkg.totalExVat));
      this.setText('[data-review-deposit]', this.money(pkg.depositExVat));
      this.setText('[data-review-vat]', this.money(depositVat));
      this.setText('[data-review-checkout]', pkg.estimatedCheckoutInclVat ? this.money(pkg.estimatedCheckoutInclVat) : 'Nog geen betaling');
      this.setText('[data-review-remaining]', this.money(pkg.remainingExVat));

      const prioritiesWrap = q(this.root, '[data-summary-priorities-wrap]');
      const list = q(this.root, '[data-summary-priorities]');
      if (prioritiesWrap) prioritiesWrap.hidden = !priorities.length;
      if (list) {
        list.replaceChildren();
        priorityLabels.forEach((priority) => {
          const li = document.createElement('li');
          li.textContent = priority;
          list.appendChild(li);
        });
      }

      this.setText('[data-review-package]', pkg.displayName);
      this.setText(
        '[data-review-duration]',
        pkg.durationMinutes ? `${pkg.durationMinutes} minuten · Cyberwetgeving QuickCheck inbegrepen` : 'Intake, training en actieplan'
      );
      this.setText('[data-review-moment]', moment);
      this.setText('[data-review-delivery]', delivery);
      this.setText(
        '[data-review-participants]',
        participants ? `${participants} deelnemer${int(participants) === 1 ? '' : 's'}` : 'Nog invullen'
      );
      this.setText('[data-review-company]', company);
      this.setText('[data-review-topics]', priorityLabels.length ? priorityLabels.join(' · ') : 'Nog kiezen');
      this.updateRouteCopy();
      this.updateMobileBar();
    }

    companyTypeLabel() {
      const field = this.field('company_type');
      return field?.selectedOptions?.[0]?.value
        ? field.selectedOptions[0].textContent.trim()
        : '';
    }

    // Keep backend values unchanged while customer-facing summaries use the new,
    // plain-language label attached to the selected card.
    selectedChoiceLabel(name, value = '') {
      const inputs = qa(this.form, `input[name="${name}"]:checked`);
      const input = value ? inputs.find((candidate) => candidate.value === value) : inputs[0];
      const label = input?.closest('label');
      return label?.querySelector('strong, em')?.textContent?.trim() || value || '';
    }

    deliveryLabel() {
      const value = this.value('delivery_type');
      if (value === 'on_location') {
        const location = this.locationString();
        return location ? `Bij jullie organisatie · ${location}` : 'Bij jullie organisatie';
      }
      if (value === 'decide_later') return 'IdenScan regelt een trainingslocatie';
      return 'Nog kiezen';
    }

    // Dutch postcode, normalised to "1234 AB". Returns '' when not a valid NL
    // postcode, so callers can validate calmly instead of sending junk.
    normalizePostcode(value) {
      const raw = String(value || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
      const match = raw.match(/^(\d{4})([A-Z]{2})$/);
      return match ? `${match[1]} ${match[2]}` : '';
    }

    // Strip stray punctuation (e.g. the ";" in "assendel;ft") while keeping
    // legitimate street/city characters, spaces, hyphens and apostrophes.
    cleanAddressPart(value, maxLength = 160) {
      return this.clean(value, maxLength)
        .replace(/[^0-9A-Za-zÀ-ÿ .'-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    locationParts() {
      return {
        street: this.cleanAddressPart(this.value('location_street'), 160),
        postcode: this.normalizePostcode(this.value('location_postcode_final')),
        city: this.cleanAddressPart(this.value('location_city_final'), 100)
      };
    }

    // One clean location line for the summary and the booking e-mail.
    locationString() {
      const parts = this.locationParts();
      const line = [parts.postcode, parts.city].filter(Boolean).join(' ');
      return [parts.street, line].filter(Boolean).join(', ');
    }

    intakeMomentLabel() {
      const dates = [this.value('preferred_date_1'), this.value('preferred_date_2')].filter(Boolean);
      return dates.length ? dates.join(' of ') : (this.value('preferred_timeframe') || 'Na persoonlijk contact');
    }

    checkStatusLabel() {
      const value = this.value('check_status');
      if (value === 'completed') return 'Terugkoppeling ontvangen';
      if (value === 'spoken') return 'Al met IdenScan gesproken';
      if (value === 'pending') return 'In beoordeling';
      return 'Niet verplicht';
    }

    paymentNote() {
      if (!this.requiresPayment()) {
        return 'Je betaalt nu niets. IdenScan bevestigt eerst scope, uitvoering, planning en prijs.';
      }
      if (this.isInvoicePayment()) {
        return 'Na serverbevestiging wordt de officiële factuur per e-mail verstuurd.';
      }
      if (!this.isDepositPayment()) return 'Kies online aanbetaling of zakelijk op factuur.';
      const pkg = this.state.package;
      const amount = pkg?.estimatedCheckoutInclVat ? `${this.money(pkg.estimatedCheckoutInclVat)} incl. btw` : 'de aanbetaling';
      if (this.state.route === 'direct') {
        if (!this.state.slot) return 'Kies eerst een beschikbaar moment.';
        return `Je rondt daarna de aanbetaling van ${amount} veilig af.`;
      }
      return `Je rondt daarna de aanbetaling van ${amount} veilig af. De definitieve scope en prijs worden persoonlijk bevestigd.`;
    }

    // A booking collects a deposit today when the package has a deposit + a valid
    // Stripe link, except when a direct package overflowed into a personal-quote
    // route (large group or no live agenda).
    requiresPayment() {
      const pkg = this.state.package;
      if (!pkg || !(pkg.depositExVat > 0) || !['start', 'team', 'action'].includes(pkg.id)) return false;
      // Only a direct booking collects a Stripe deposit at submit. Any generic
      // large-group/availability overflow remains intake-first.
      return this.state.route === 'direct';
    }

    updateSubmitState() {
      const button = q(this.root, '[data-submit]');
      if (!button || this.submitLocked) return;

      // The confirm CTA is always active on the review step; the only time it is
      // disabled is transiently during sending (see setLoading). Missing input —
      // a slot, valid postcode, consent — is caught on click by validateFinal,
      // which routes to the offending step and focuses the first invalid field.
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      this.updateRouteCopy();
    }

    async submit(event) {
      if (this.nativeSubmitting) return;
      event.preventDefault();
      if (this.submitLocked || !this.validateFinal()) return;

      this.captureState();
      this.submitLocked = true;
      this.setLoading(true);
      this.dispatch('submit-start', {
        route: this.state.route,
        packageId: this.state.package?.id || null
      });

      try {
        if (this.state.route === 'direct' && this.isInvoicePayment()) await this.submitInvoice();
        else if (this.state.route === 'direct') await this.submitDirect();
        else await this.submitIntake();
      } catch (error) {
        if (!this.isLifecycleAbort(error)) {
          // Log only the error code — never the payload or any personal data.
          console.error('[IdenScan booking v5] submit failed:', error?.code || 'unknown');
          // A 409 slot/reference conflict is a normal race, not a form error:
          // reload the agenda, keep the visitor's data, and route back to step 2.
          if (error?.code === 'slot_unavailable' || Number(error?.details?.status) === 409) {
            const conflictMessage = this.handleBookingConflict(error);
            this.setText('[data-submit-status]', 'Het gekozen moment was niet meer vrij. Kies opnieuw een beschikbaar moment.');
            this.dispatch('error', { code: error?.code || 'slot_unavailable', message: conflictMessage });
          } else {
            this.applyBackendFieldErrors(error);
            const customerError = this.customerError(error);
            this.finalError(customerError.message, customerError.title);
            const paymentFailure = ['checkout_url_invalid', 'checkout_response_invalid', 'checkout_api_missing']
              .includes(String(error?.code || ''));
            this.setText(
              '[data-submit-status]',
              paymentFailure
                ? 'De betaling kon niet worden gestart. Probeer het opnieuw of neem contact met IdenScan op.'
                : 'De aanvraag is niet verzonden. Controleer de gemarkeerde velden en probeer het opnieuw.'
            );
            this.dispatch('error', { code: error?.code || 'unknown', message: customerError.message });
          }
        }
        this.submitLocked = false;
        this.setLoading(false);
      }
    }

    // Map a backend 422 { fieldErrors: {...} } onto the matching form fields,
    // show a calm inline message under each and jump to the first affected step.
    applyBackendFieldErrors(error) {
      const fieldErrors = error?.details?.payload?.fieldErrors;
      if (!fieldErrors || typeof fieldErrors !== 'object') return false;
      const map = {
        name: { field: 'contact_name', step: 3 },
        company: { field: 'company_name', step: 3 },
        email: { field: 'business_email', step: 3 },
        participants: { field: 'participants', step: 2 },
        invoiceCompanyName: { field: 'invoiceCompanyName', step: 4 },
        invoiceEmail: { field: 'invoiceEmail', step: 4 },
        invoiceAddress: { field: 'invoiceAddress', step: 4 },
        invoicePostalCode: { field: 'invoicePostalCode', step: 4 },
        invoiceCity: { field: 'invoiceCity', step: 4 },
        invoiceCountry: { field: 'invoiceCountry', step: 4 },
        vatNumber: { field: 'vatNumber', step: 4 },
        kvkNumber: { field: 'kvkNumber', step: 4 },
        poNumber: { field: 'poNumber', step: 4 }
      };
      let firstTarget = null;
      Object.keys(fieldErrors).forEach((key) => {
        const messages = {
          email: 'Vul een geldig zakelijk e-mailadres in.',
          invoiceCompanyName: 'Vul de bedrijfsnaam voor de factuur in.',
          invoiceEmail: 'Vul een geldig factuur e-mailadres in.',
          invoiceAddress: 'Vul het factuuradres in.',
          invoicePostalCode: 'Vul de postcode voor de factuur in.',
          invoiceCity: 'Vul de plaats voor de factuur in.',
          invoiceCountry: 'Vul het land voor de factuur in.',
          vatNumber: 'Controleer het btw-nummer.',
          kvkNumber: 'Controleer het KvK-nummer.',
          poNumber: 'Controleer het PO-/inkoopnummer.'
        };
        const message = messages[key] || 'Controleer dit veld.';
        const entry = map[key];
        if (entry) {
          const field = this.field(entry.field);
          if (field) {
            field.classList.add('is-invalid');
            field.setAttribute('aria-invalid', 'true');
            this.setFieldMessage(field, message);
            if (!firstTarget) firstTarget = { field, step: entry.step };
          }
        } else if (key === 'package') {
          this.showInlineError('[data-package-error]');
          if (!firstTarget) firstTarget = { step: 1 };
        } else if (key === 'date' || key === 'time') {
          this.showInlineError('[data-slot-error]');
          if (!firstTarget) firstTarget = { step: 2 };
        }
      });
      if (!firstTarget) return false;
      this.goToStep(firstTarget.step);
      if (firstTarget.field) {
        try { firstTarget.field.focus({ preventScroll: true }); } catch (_) { /* hidden control */ }
        this.scrollTo(firstTarget.field.closest('label, fieldset') || firstTarget.field, 110);
      }
      return true;
    }

    async submitDirect() {
      // The locally generated agenda drives bookability; we validate the chosen
      // moment against the current local slots below. No blocking remote
      // availability round-trip is required to start checkout.
      this.captureState();
      if (this.state.route !== 'direct') {
        this.goToStep(2);
        throw new BookingError(
          'route_changed',
          'De directe boeking is niet meer beschikbaar. Geef jullie voorkeursplanning door.'
        );
      }

      const slot = this.findCurrentSlot();
      if (!slot?.available) {
        this.clearSelectedSlot();
        this.renderAvailability();
        this.goToStep(2);
        throw new BookingError('slot_unavailable', 'Dit moment is inmiddels bezet. Kies een ander beschikbaar moment.');
      }

      const reference = this.ensureBookingReference();
      // Every direct package (Start, Team, Actieplan) goes through the dynamic
      // server-side Checkout Session at POST /api/training-checkout, which carries
      // the COMPLETE booking + intake payload into Stripe metadata so the internal
      // confirmation mail contains every answer. No fixed Payment Link is used for a
      // fully completed training booking (that would drop all intake fields).
      await this.submitTrainingCheckout({ reference, slot });
    }

    async submitInvoice() {
      this.captureState();
      if (!this.invoiceAvailable() || !this.config.api.invoice) {
        throw new BookingError('invoice_api_missing', 'Zakelijk op factuur is voor deze boeking niet beschikbaar.');
      }

      const slot = this.findCurrentSlot();
      if (!slot?.available) {
        this.clearSelectedSlot();
        this.renderAvailability();
        this.goToStep(2);
        throw new BookingError('slot_unavailable', 'Dit moment is inmiddels bezet. Kies een ander beschikbaar moment.');
      }

      const bookingReference = this.safeReference(this.ensureBookingReference());
      const payload = {
        ...this.buildBookingPayload({ bookingReference, slot }),
        invoiceCompanyName: this.clean(this.value('invoiceCompanyName'), 200),
        invoiceEmail: this.clean(this.value('invoiceEmail'), 254),
        invoiceAddress: this.clean(this.value('invoiceAddress'), 300),
        invoicePostalCode: this.clean(this.value('invoicePostalCode'), 20),
        invoiceCity: this.clean(this.value('invoiceCity'), 120),
        invoiceCountry: this.clean(this.value('invoiceCountry'), 60),
        vatNumber: this.clean(this.value('vatNumber'), 40),
        kvkNumber: this.clean(this.value('kvkNumber'), 40),
        poNumber: this.clean(this.value('poNumber'), 60)
      };
      this.setText('[data-submit-status]', 'Boeking en factuur veilig voorbereiden…');

      // The invoice endpoint itself is the payment-method contract. It accepts
      // Content-Type only in CORS and is idempotent on bookingReference, so we do
      // not invent a paymentMethod field or add deposit-only request headers.
      const response = await this.requestJSON(this.config.api.invoice, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });

      const returnedReference = this.safeReference(response?.bookingReference || '');
      if (
        response?.success !== true
        || response?.invoice !== true
        || response?.paymentStatus !== 'invoice_open'
        || returnedReference !== bookingReference
      ) {
        throw new BookingError('invoice_response_invalid', 'De factuurboeking is niet bevestigd door de server.');
      }

      this.state.bookingReference = bookingReference;
      this.clearSupersede();
      this.showInvoiceReceivedState(bookingReference, slot);
      this.track(this.config.analytics.events.bookingCreated, {
        booking_reference: bookingReference,
        item_id: this.state.package?.id || '',
        route: this.state.route,
        payment_method: 'invoice'
      });
      this.dispatch('invoice-received', {
        reference: bookingReference,
        packageId: this.state.package?.id || '',
        slotId: slot.id || ''
      });
    }

    async submitIntake() {
      const reference = this.ensureBookingReference();
      await this.submitBookingRequest({ reference, slot: null });
    }

    // The browser supplies choices and contact data, never prices or a Payment
    // Link decision. The backend validates the package, atomically holds the slot,
    // stores the request, queues both mails and returns the one allowed redirect.
    async submitBookingRequest({ reference, slot }) {
      if (!this.config.api.booking) {
        throw new BookingError(
          'booking_api_missing',
          'De beveiligde boekingskoppeling is nog niet beschikbaar. Neem contact op met info@idenscan.com.'
        );
      }

      const bookingReference = this.safeReference(reference);
      const packageId = this.state.package?.id || '';
      const payload = this.buildBookingPayload({ bookingReference, slot });
      this.setText('[data-submit-status]', 'Aanvraag veilig opslaan en het moment controleren…');

      const response = await this.requestJSON(this.config.api.booking, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Idempotency-Key': bookingReference,
          'X-IdenScan-Booking-Version': VERSION
        },
        body: JSON.stringify(payload)
      });

      // The backend is the single source of truth: it selects the fixed Stripe
      // Payment Link from its own server-side allowlist and returns the final
      // checkoutUrl. We only redirect when success === true and a valid booking
      // reference came back — never on a partial or spam-dropped response.
      if (response?.success !== true) {
        throw new BookingError('booking_response_invalid', 'De aanvraag is niet bevestigd door de server.');
      }
      const returnedReference = this.safeReference(
        response?.bookingReference || response?.booking_reference || response?.booking?.bookingReference || ''
      );
      if (!returnedReference || returnedReference !== bookingReference) {
        throw new BookingError('booking_response_invalid', 'De server gaf geen geldige boekingsreferentie terug.');
      }

      // A genuine intake response returns NO checkout URL. Confirm receipt and
      // stop; never redirect.
      if (response?.intake === true) {
        this.state.bookingReference = bookingReference;
        this.clearSupersede();
        this.showIntakeReceivedState(bookingReference);
        this.track(this.config.analytics.events.intakeSubmitted, {
          booking_reference: bookingReference,
          item_id: packageId,
          route: this.state.route
        });
        this.dispatch('intake-received', { reference: bookingReference, packageId });
        return;
      }

      // Direct packages: the backend created a server-side Stripe Checkout Session and
      // returns its URL. The frontend NEVER builds a checkout URL — it only
      // validates that the backend pointed at a real Stripe Checkout host/path.
      const checkoutUrl = response?.checkoutUrl || response?.checkout_url
        || response?.redirectUrl || response?.redirect_url || response?.booking?.checkoutUrl;
      const stripeUrl = this.validateBackendStripeRedirect(checkoutUrl, packageId, returnedReference);
      if (!stripeUrl) {
        throw new BookingError('checkout_url_invalid', 'De server gaf geen veilige Stripe-betaallink terug.');
      }

      this.state.bookingReference = bookingReference;
      this.clearSupersede();
      this.showPaymentRedirectState(bookingReference, stripeUrl);
      this.track(this.config.analytics.events.bookingCreated, {
        booking_reference: bookingReference,
        item_id: packageId,
        route: this.state.route
      });
      this.trackBeginCheckout({ reference: bookingReference });
      this.dispatch('checkout-ready', { reference: bookingReference, packageId, slotId: slot?.id || '' });
      window.setTimeout(() => window.location.assign(stripeUrl), 900);
    }

    // Direct Start/Team/Action: Neon-free server-side Stripe Checkout Session.
    // POSTs the SAME complete payload as submitBookingRequest (incl. all 17 Step-3
    // fields) to /api/training-checkout, which carries every field through Stripe
    // metadata for the internal mail and returns a Checkout Session URL. Fails
    // closed; the frontend never builds a checkout URL and no PII goes in the URL.
    async submitTrainingCheckout({ reference, slot }) {
      if (!this.config.api.checkout) {
        throw new BookingError(
          'checkout_api_missing',
          'De beveiligde betaalkoppeling is nog niet beschikbaar. Neem contact op met info@idenscan.com.'
        );
      }

      const bookingReference = this.safeReference(reference);
      const packageId = this.state.package?.id || '';
      const payload = this.buildBookingPayload({ bookingReference, slot });
      this.setText('[data-submit-status]', 'Beveiligde betaling voorbereiden…');

      const response = await this.requestJSON(this.config.api.checkout, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Idempotency-Key': bookingReference,
          'X-IdenScan-Booking-Version': VERSION
        },
        body: JSON.stringify(payload)
      });

      if (response?.success !== true) {
        throw new BookingError('checkout_response_invalid', 'De betaling is niet bevestigd door de server.');
      }
      const returnedReference = this.safeReference(
        response?.bookingReference || response?.booking_reference || ''
      );
      if (!returnedReference || returnedReference !== bookingReference) {
        throw new BookingError('checkout_response_invalid', 'De server gaf geen geldige boekingsreferentie terug.');
      }

      // The current booking-api contract returns exactly `checkoutUrl`. Never
      // construct a payment URL in the browser or guess response aliases.
      const checkoutUrl = response?.checkoutUrl;
      const stripeUrl = this.validateBackendStripeRedirect(checkoutUrl, packageId, returnedReference);
      if (!stripeUrl) {
        throw new BookingError('checkout_url_invalid', 'De server gaf geen veilige Stripe-betaallink terug.');
      }

      this.state.bookingReference = bookingReference;
      this.clearSupersede();
      this.showPaymentRedirectState(bookingReference, stripeUrl);
      this.track(this.config.analytics.events.bookingCreated, {
        booking_reference: bookingReference,
        item_id: packageId,
        route: this.state.route
      });
      this.trackBeginCheckout({ reference: bookingReference });
      this.dispatch('checkout-ready', { reference: bookingReference, packageId, slotId: slot?.id || '' });
      window.setTimeout(() => window.location.assign(stripeUrl), 900);
    }

    buildBookingPayload({ bookingReference, slot }) {
      const pkg = this.state.package;
      return {
        version: VERSION,
        bookingReference,
        // When the visitor changed slot/package after a hold was created, tell the
        // backend which reference to release first (immutable reference contract).
        supersedes: this.state.supersedes && this.state.supersedes !== bookingReference ? this.state.supersedes : '',
        packageId: pkg?.id || '',
        packageName: pkg?.name || '',
        slotVariantId: slot?.id || '',
        date: slot?.date || this.value('preferred_date_1'),
        startTime: slot?.startTime || '',
        endTime: slot?.endTime || '',
        // Backend-contract aliases: /api/training-booking reads `email` and `time`.
        // These mirror businessEmail/startTime so frontend and backend line up.
        email: this.clean(this.value('business_email'), 160),
        time: slot?.startTime || '',
        participants: int(this.value('participants')),
        deliveryType: this.value('delivery_type'),
        contactName: this.clean(this.value('contact_name'), 100),
        businessEmail: this.clean(this.value('business_email'), 160),
        phone: this.clean(this.value('phone'), 30),
        companyName: this.clean(this.value('company_name'), 120),
        // The backend uses the `website` key as a spam honeypot, so we send the
        // hidden honeypot value there and keep the real company site separate.
        website: this.clean(this.value('company_fax'), 200),
        companyWebsite: this.normalizeWebsite(this.value('website')),
        employeeCount: this.clean(this.value('employee_count'), 30),
        companyType: this.clean(this.value('company_type'), 60),
        departments: this.selectedValues('departments').map((value) => this.clean(value, 100)),
        priorities: this.selectedValues('priorities').map((value) => this.clean(value, 120)),
        // Step 3 guided intake — captured so the backend can attune examples,
        // exercises and the included knowledge test to the customer's daily work.
        dailyTools: this.selectedValues('daily_tools').map((value) => this.clean(value, 80)),
        dailyToolsOther: this.clean(this.value('daily_tools_other'), 120),
        businessSystems: this.selectedValues('business_systems').map((value) => this.clean(value, 80)),
        businessSystemsOther: this.clean(this.value('business_systems_other'), 120),
        dataTypes: unique(this.selectedValues('data_types').map((value) => this.clean(value, 80)).filter(Boolean)),
        dataTypesOther: this.clean(this.value('data_types_other'), 120),
        aiUsage: this.clean(this.value('ai_usage'), 40),
        aiTools: this.selectedValues('ai_tools').map((value) => this.clean(value, 80)),
        aiToolsOther: this.clean(this.value('ai_tools_other'), 120),
        aiTasks: this.selectedValues('ai_tasks').map((value) => this.clean(value, 80)),
        paymentRoles: this.clean(this.value('payment_roles'), 60),
        paymentControl: this.clean(this.value('payment_control'), 60),
        reportingRoute: this.clean(this.value('reporting_route'), 60),
        checkStatus: this.clean(this.value('check_status'), 30),
        incidentExperience: this.clean(this.value('incident_experience'), 80),
        trainingGoal: this.clean(this.value('training_goal'), 700),
        preferredDate2: this.value('preferred_date_2'),
        preferredTimeframe: this.clean(this.value('preferred_timeframe'), 120),
        contactPreference: this.value('intake_contact_preference'),
        location: this.value('delivery_type') === 'on_location' ? {
          street: this.clean(this.value('location_street'), 160),
          postcode: this.clean(this.value('location_postcode_final'), 12),
          city: this.clean(this.value('location_city_final'), 100)
        } : null,
        billing: {
          purchaseReference: this.clean(this.value('purchase_reference'), 120),
          invoiceNote: this.clean(this.value('invoice_note'), 180)
        },
        consent: {
          detailsConfirmed: Boolean(this.field('details_confirmed')?.checked),
          privacyConfirmed: Boolean(this.field('privacy_confirmed')?.checked),
          termsConfirmed: Boolean(this.field('terms_confirmed')?.checked),
          confirmedAt: new Date().toISOString()
        },
        source: {
          page: this.clean(this.value('source_page') || window.location.href, 500),
          sectionId: this.config.sectionId
        },
        antiAbuse: { companyFax: this.clean(this.value('company_fax'), 200) }
      };
    }

    validateBackendStripeRedirect(value, packageId, bookingReference) {
      // The frontend is deliberately NOT the source of truth for payment URLs.
      // It only verifies that the booking-api returned an HTTPS URL on Stripe's
      // exact hosted Checkout origin for a known direct package/reference.
      //
      // Do not pin the validator to one Stripe path such as /c/pay/: Stripe can
      // change the hosted Checkout path/fragment while the trusted origin remains
      // checkout.stripe.com. The previous path pin caused valid server-created
      // Checkout Sessions to be rejected as `checkout_url_invalid`.
      if (!['start', 'team', 'action'].includes(packageId)) return '';
      if (!/^TRB-\d{8}-[A-Z0-9]{6}$/.test(String(bookingReference || ''))) return '';

      try {
        const url = new URL(String(value || '').trim());
        if (url.protocol !== 'https:') return '';
        if (url.hostname !== 'checkout.stripe.com') return '';
        if (url.username || url.password) return '';
        if (url.port && url.port !== '443') return '';

        // A hosted Checkout URL must contain a non-root path. We intentionally
        // preserve Stripe's own query string and hash because Checkout may require
        // them to restore/session-bind the hosted page.
        if (!url.pathname || url.pathname === '/') return '';

        // Defensive: never follow one of the known CyberCheck payment links even
        // if a future redirect format unexpectedly embeds such an identifier.
        if (FORBIDDEN_CYBERCHECK_PAYMENT_LINK_IDS.some((id) => url.href.includes(id))) return '';

        return url.toString();
      } catch (_) {
        return '';
      }
    }

    showPaymentRedirectState(bookingReference, stripeUrl) {
      this.showCheckoutSuccessState();
      this.setText('[data-checkout-success-message]', 'Jullie gegevens zijn ontvangen. Rond nu de aanbetaling af om de trainingsaanvraag te bevestigen…');
      const reference = q(this.root, '[data-checkout-success-reference]');
      if (reference) {
        reference.hidden = false;
        const strong = q(reference, 'strong');
        if (strong) strong.textContent = bookingReference;
      }
      const paymentButton = q(this.root, '[data-payment-fallback]');
      if (paymentButton) {
        paymentButton.href = stripeUrl;
        paymentButton.hidden = false;
      }
      const contactButton = q(this.root, '[data-contact-fallback]');
      if (contactButton) contactButton.hidden = true;
    }

    showBookingSuccessWithoutRedirect() {
      this.showCheckoutSuccessState();
      const spinner = q(this.root, '[data-checkout-success-spinner]');
      if (spinner) spinner.hidden = true;
      this.setText(
        '[data-checkout-success-message]',
        'We hebben jullie aanvraag ontvangen, maar konden de betaalpagina niet automatisch openen. Gebruik de knop hieronder of neem contact op met info@idenscan.com.'
      );
      const reference = q(this.root, '[data-checkout-success-reference]');
      if (reference) reference.hidden = true;
      const paymentButton = q(this.root, '[data-payment-fallback]');
      if (paymentButton) {
        paymentButton.hidden = true;
        paymentButton.removeAttribute('href');
      }
      const contactButton = q(this.root, '[data-contact-fallback]');
      if (contactButton) contactButton.hidden = false;
    }

    showCheckoutSuccessState() {
      this.steps.forEach((step) => {
        step.hidden = true;
        step.classList.remove('is-active');
        step.setAttribute('aria-hidden', 'true');
        if ('inert' in step) step.inert = true;
      });
      const panel = q(this.root, '[data-checkout-success]');
      if (panel) panel.hidden = false;
      this.toggleAll('[data-mobile-bar]', false);
      this.root.dataset.checkoutState = 'success';
      this.submitLocked = true;
      const heading = q(panel, 'h2');
      heading?.focus({ preventScroll: true });
      if (panel) this.scrollTo(panel, 40);
    }

    showInvoiceReceivedState(bookingReference, slot) {
      this.steps.forEach((step) => {
        step.hidden = true;
        step.classList.remove('is-active');
        step.setAttribute('aria-hidden', 'true');
        if ('inert' in step) step.inert = true;
      });
      const checkoutPanel = q(this.root, '[data-checkout-success]');
      if (checkoutPanel) checkoutPanel.hidden = true;
      const panel = q(this.root, '[data-invoice-success]');
      if (!panel) {
        this.showCheckoutSuccessState();
        this.setText('[data-checkout-success-message]', 'Boeking ontvangen. De officiële factuur wordt per e-mail verstuurd.');
        return;
      }
      panel.hidden = false;
      this.setText('[data-invoice-success-reference]', bookingReference);
      this.setText('[data-invoice-success-package]', this.state.package?.displayName || this.state.package?.name || '—');
      this.setText('[data-invoice-success-moment]', slot ? this.slotLabel(slot) : '—');
      this.setText('[data-invoice-success-company]', this.value('invoiceCompanyName') || this.value('company_name') || '—');
      this.toggleAll('[data-mobile-bar]', false);
      this.root.dataset.checkoutState = 'invoice-success';
      this.submitLocked = true;
      const heading = q(panel, 'h2');
      heading?.focus({ preventScroll: true });
      this.scrollTo(panel, 40);
    }

    // A single, opaque booking reference (TRB-...). No personal data is encoded.
    ensureBookingReference() {
      if (!this.state.bookingReference) {
        this.state.bookingReference = this.createBookingReference();
        const hidden = this.field('booking_reference');
        if (hidden) hidden.value = this.state.bookingReference;
      }
      return this.state.bookingReference;
    }

    createBookingReference() {
      const random = typeof crypto?.randomUUID === 'function'
        ? crypto.randomUUID().replace(/-/g, '')
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      return `TRB-${date}-${random.toUpperCase().slice(0, 6)}`;
    }

    clearSupersede() {
      this.state.supersedes = '';
    }

    // The booking reference is immutable server-side. When the visitor changes
    // their slot/package after a hold exists, we mint a NEW reference and remember
    // the old one as `supersedes`, so the backend releases the old hold + expires
    // its Stripe session before creating the new one.
    regenerateBookingReference() {
      const previous = this.state.bookingReference;
      if (previous) this.state.supersedes = previous;
      this.state.bookingReference = this.createBookingReference();
      const hidden = this.field('booking_reference');
      if (hidden) hidden.value = this.state.bookingReference;
      return this.state.bookingReference;
    }

    // A 409 slot/reference conflict: the moment was taken (or the reference is
    // locked to another slot). Reload the live agenda, keep every contact field,
    // send the visitor back to the moment step with a fresh reference + supersede,
    // and never silently drop their input.
    handleBookingConflict(error) {
      const payloadError = String(error?.details?.payload?.error || '').toUpperCase();
      this.regenerateBookingReference();
      this.clearSelectedSlot();
      // Refresh from Neon so the just-taken moment disappears; if the API is down,
      // the calendar remains fail-closed.
      void this.refreshRemoteAvailability();
      this.renderAvailability();
      this.goToStep(2);
      const message = payloadError === 'REFERENCE_LOCKED'
        ? 'Je koos een ander moment. Bevestig het nieuwe moment opnieuw.'
        : 'Dit moment is net door iemand anders geboekt. Kies een ander beschikbaar moment.';
      this.showInlineError('[data-slot-error]', message);
      this.toast(message, true);
      return message;
    }

    showIntakeReceivedState(bookingReference) {
      this.showCheckoutSuccessState();
      const spinner = q(this.root, '[data-checkout-success-spinner]');
      if (spinner) spinner.hidden = true;
      this.setText(
        '[data-checkout-success-message]',
        'We hebben jullie intakeaanvraag goed ontvangen. IdenScan neemt persoonlijk contact op om scope, planning en de definitieve prijs af te stemmen. De aanbetaling van €250 excl. btw volgt pas na deze intake.'
      );
      const reference = q(this.root, '[data-checkout-success-reference]');
      if (reference) {
        reference.hidden = false;
        const strong = q(reference, 'strong');
        if (strong) strong.textContent = bookingReference;
      }
      const paymentButton = q(this.root, '[data-payment-fallback]');
      if (paymentButton) { paymentButton.hidden = true; paymentButton.removeAttribute('href'); }
      const contactButton = q(this.root, '[data-contact-fallback]');
      if (contactButton) contactButton.hidden = false;
    }

    // Joins a checkbox group (plus an optional free-text "other" field) into one
    // readable line for the intake summary. Used by the step 3 preparation flow.
    intakeList(name, otherName) {
      const values = this.selectedValues(name).map((value) => this.clean(value, 80)).filter(Boolean);
      if (otherName) {
        const other = this.clean(this.value(otherName), 120);
        if (other) values.push(other);
      }
      return values.length ? values.join(', ') : 'Niet ingevuld';
    }

    // Everything the shop mailbox needs, as plain contact-form fields. No personal
    // data ever leaves through the Stripe URL — only this reference does.
    buildContactFields({ reference, slot }) {
      const pkg = this.state.package;
      const dateFmt = new Intl.DateTimeFormat('nl-NL', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
      const timeFmt = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' });

      const dateLabel = slot
        ? dateFmt.format(slot.startsAt)
        : ([this.value('preferred_date_1'), this.value('preferred_date_2')].filter(Boolean).join(' of ')
          || this.value('preferred_timeframe') || 'Nog af te stemmen');
      const timeLabel = slot
        ? `${timeFmt.format(slot.startsAt)}${slot.endsAt ? ' – ' + timeFmt.format(slot.endsAt) : ''} uur`
        : (this.value('preferred_timeframe') || 'Nog af te stemmen');

      const companyName = this.clean(this.value('company_name'), 120);
      const contactName = this.clean(this.value('contact_name'), 100);
      const businessEmail = this.clean(this.value('business_email'), 160);
      const phone = this.clean(this.value('phone'), 30);
      const participants = this.value('participants');
      const priorities = this.selectedValues('priorities');
      const departments = this.selectedValues('departments');
      const pays = this.requiresPayment();

      const lines = [
        `Boekingsreferentie: ${reference}`,
        `Status: ${this.state.route === 'direct' ? 'Direct gekozen moment' : 'Voorkeursplanning (persoonlijke bevestiging)'}`,
        `Pakket: ${pkg?.name || '—'}`,
        `Datum: ${dateLabel}`,
        `Begintijd: ${slot ? (slot.startTime || timeFmt.format(slot.startsAt)) : timeLabel}`,
        `Eindtijd: ${slot ? (slot.endTime || (slot.endsAt ? timeFmt.format(slot.endsAt) : 'Nog af te stemmen')) : 'Nog af te stemmen'}`,
        `Uitvoering: ${this.deliveryLabel()}`,
        '',
        `Bedrijfsnaam: ${companyName || '—'}`,
        `Contactpersoon: ${contactName || '—'}`,
        `Zakelijk e-mailadres: ${businessEmail || '—'}`,
        `Telefoon: ${phone || 'Niet ingevuld'}`,
        `Website: ${this.normalizeWebsite(this.value('website')) || 'Niet ingevuld'}`,
        `Organisatiegrootte: ${this.field('employee_count')?.selectedOptions?.[0]?.textContent?.trim() || 'Niet ingevuld'}`,
        `Type organisatie: ${this.companyTypeLabel() || 'Niet ingevuld'}`,
        `Deelnemers: ${participants || '—'}`,
        `Rollen/afdelingen: ${departments.length ? departments.join(', ') : 'Niet ingevuld'}`,
        `E-mail- en samenwerkingssoftware: ${this.intakeList('daily_tools', 'daily_tools_other')}`,
        `Bedrijfssoftware: ${this.intakeList('business_systems', 'business_systems_other')}`,
        `Soorten informatie: ${this.intakeList('data_types', 'data_types_other')}`,
        `Praktijksituatie: ${this.selectedChoiceLabel('payment_control') || 'Niet ingevuld'}`,
        `AI-gebruik: ${this.selectedChoiceLabel('ai_usage') || 'Niet ingevuld'}`,
        `Gebruikte AI-toepassingen: ${this.intakeList('ai_tools', 'ai_tools_other')}`,
        `Huidige reactie op iets verdachts: ${this.selectedChoiceLabel('reporting_route') || 'Niet ingevuld'}`,
        `Grootste risico: ${this.selectedChoiceLabel('incident_experience') || 'Niet ingevuld'}`,
        `Belangrijkste trainingsdoel: ${priorities.length ? priorities.map((value) => this.selectedChoiceLabel('priorities', value)).join(', ') : 'Niet ingevuld'}`,
        `Eigen situatie: ${this.clean(this.value('training_goal'), 700) || 'Niet ingevuld'}`,
        '',
        `Totaalbedrag (excl. btw): ${pkg ? this.money(pkg.totalExVat) : '—'}`,
        `Aanbetaling (excl. btw): ${pkg ? this.money(pkg.depositExVat) : '—'}`,
        `Aanbetaling via Stripe vandaag: ${pays && pkg?.estimatedCheckoutInclVat ? `${this.money(pkg.estimatedCheckoutInclVat)} incl. btw` : 'Geen betaling nu'}`,
        `Resterend bedrag (excl. btw): ${pkg ? this.money(pkg.remainingExVat) : '—'}`,
        '',
        `Inkoop-/factuurreferentie: ${this.clean(this.value('purchase_reference'), 120) || 'Niet ingevuld'}`,
        `Opmerking facturatie: ${this.clean(this.value('invoice_note'), 180) || 'Niet ingevuld'}`,
        `Team Risico Check: ${this.checkStatusLabel()}`
      ];

      if (this.value('delivery_type') === 'on_location') {
        lines.push('', `Trainingslocatie: ${this.locationString() || 'Nog af te stemmen'}`);
      }

      return {
        'contact[email]': businessEmail,
        'contact[Naam]': contactName,
        'contact[Telefoon]': phone,
        'contact[Boekingsreferentie]': reference,
        'contact[Onderwerp]': `Nieuwe trainingsaanvraag ${reference} — ${pkg?.name || 'IdenScan'}`,
        'contact[body]': lines.join('\n')
      };
    }

    renderIntakeSuccess(result) {
      const step = this.steps.find((candidate) => int(candidate.dataset.step) === 4);
      if (!step) return;
      const reference = result.reference || result.bookingId || 'niet beschikbaar';
      step.replaceChildren();

      const wrapper = document.createElement('div');
      wrapper.className = 'isb-v4__panel isb-v4__panel--accent';
      const eyebrow = document.createElement('span');
      eyebrow.textContent = 'Aanvraag ontvangen';
      const heading = document.createElement('h2');
      heading.tabIndex = -1;
      heading.textContent = 'Jullie intakeaanvraag is veilig ontvangen';
      const paragraph = document.createElement('p');
      paragraph.textContent = 'IdenScan controleert scope, groepsindeling en planning en reageert meestal binnen één werkdag.';
      const referenceNode = document.createElement('p');
      referenceNode.textContent = `Referentie: ${reference}`;
      const link = document.createElement('a');
      link.className = 'isb-v4__button isb-v4__button--primary';
      link.href = this.safeSameSiteUrl(result.redirectUrl)
        || this.config.stripe.successUrl
        || '/';
      link.textContent = 'Terug naar IdenScan';
      wrapper.append(eyebrow, heading, paragraph, referenceNode, link);
      step.appendChild(wrapper);
      this.currentStep = 4;
      this.updateProgress();
      heading.focus({ preventScroll: true });
      this.scrollTo(step, 40);
      this.submitLocked = true;
    }

    trackView() {
      const items = Object.values(this.config.packages).map((pkg, index) => this.analyticsItem(pkg, index));
      this.trackOnce('view', this.config.analytics.events.view, {
        item_list_id: 'idenscan_training_packages',
        item_list_name: 'IdenScan trainingspakketten',
        items
      });
    }

    trackPackageSelection() {
      const pkg = this.state.package;
      if (!pkg) return;
      this.track(this.config.analytics.events.selectPackage, {
        item_list_id: 'idenscan_training_packages',
        item_list_name: 'IdenScan trainingspakketten',
        items: [this.analyticsItem(pkg, 0)]
      });
    }

    trackSlotStepCompleted() {
      this.track(this.config.analytics.events.slotStepCompleted, {
        item_id: this.state.package?.id || '',
        route: this.state.route,
        delivery_type: this.value('delivery_type') || '',
        slot_selected: Boolean(this.state.slot)
      });
    }

    trackDetailsCompleted() {
      this.track(this.config.analytics.events.detailsCompleted, {
        item_id: this.state.package?.id || '',
        route: this.state.route,
        participants_band: this.participantBand(),
        department_count: this.selectedValues('departments').length,
        priority_count: this.selectedValues('priorities').length,
        incident_experience: this.value('incident_experience') || ''
      });
    }

    trackBeginCheckout(result) {
      const pkg = this.state.package;
      this.track(this.config.analytics.events.checkoutStarted, {
        currency: this.config.currency,
        value: (pkg?.estimatedCheckoutInclVat || 0) / 100,
        booking_reference: result.reference || result.bookingId,
        items: pkg ? [this.analyticsItem(pkg, 0, true)] : []
      });
    }

    analyticsItem(pkg, index = 0, checkoutValue = false) {
      return {
        item_id: `training_${pkg.id}`,
        item_name: pkg.name,
        item_category: 'Cybersecurity training',
        item_variant: pkg.route === 'direct' ? 'Direct boekbaar' : 'Intake',
        index,
        price: (checkoutValue ? pkg.estimatedCheckoutInclVat : pkg.totalExVat) / 100,
        quantity: 1
      };
    }

    trackOnce(key, eventName, parameters = {}) {
      if (this.tracked.has(key)) return;
      this.tracked.add(key);
      this.track(eventName, parameters);
    }

    track(eventName, parameters = {}) {
      if (!this.config.analytics.enabled || !eventName) return;

      const ecommerceEventNames = new Set([
        this.config.analytics.events.view,
        this.config.analytics.events.selectPackage,
        this.config.analytics.events.checkoutStarted
      ]);
      const base = {
        event: eventName,
        event_id: this.createId('evt'),
        component: this.config.analytics.component,
        booking_version: VERSION
      };
      const payload = ecommerceEventNames.has(eventName)
        ? { ...base, ecommerce: parameters }
        : { ...base, ...parameters };

      window.dataLayer = window.dataLayer || [];
      if (ecommerceEventNames.has(eventName)) {
        window.dataLayer.push({ ecommerce: null });
      }
      window.dataLayer.push(payload);
      if (this.config.analytics.debug) {
        console.info('[IdenScan booking v5][GA4]', payload);
      }
    }

    participantBand() {
      const count = int(this.value('participants'));
      if (!count) return 'unknown';
      if (count <= 5) return '1-5';
      if (count <= 10) return '6-10';
      if (count <= 25) return '11-25';
      if (count <= 50) return '26-50';
      return '51+';
    }

    attribution() {
      const params = new URLSearchParams(window.location.search);
      const keys = [
        'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
        'gclid', 'fbclid', 'gbraid', 'wbraid', 'gad_source'
      ];
      const result = {};
      keys.forEach((key) => {
        const value = this.clean(params.get(key), 250);
        if (value) result[key] = value;
      });
      return result;
    }

    scheduleSafeDraftSave() {
      // Deliberately memory-only: organization and training data is never persisted in the browser.
    }

    saveSafeDraft() {
      // Deliberately disabled. Durable drafts belong on the server, not in browser storage.
    }

    restoreValue(name, value, radio = false) {
      if (value === undefined || value === null || value === '') return;
      if (radio) {
        this.selectRadioValue(name, String(value));
        return;
      }
      const field = this.field(name);
      if (field) field.value = String(value);
    }

    restoreValues(name, values, max = 100) {
      if (!Array.isArray(values)) return;
      const selected = new Set(values.map(String).slice(0, max));
      qa(this.form, `input[name="${name}"]`).forEach((input) => {
        input.checked = selected.has(input.value);
      });
    }

    selectRadioValue(name, value) {
      const input = q(this.form, `input[name="${name}"][value="${CSS.escape(value)}"]`);
      if (input) input.checked = true;
    }

    clearSafeDraft() {
      // Nothing is stored in the browser.
    }

    setLoading(loading) {
      const button = q(this.root, '[data-submit]');
      if (!button) return;
      button.classList.toggle('is-loading', loading);
      button.setAttribute('aria-busy', String(loading));
      if (loading) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        this.setText(
          '[data-submit-label]',
          this.state.route === 'direct'
            ? (this.isInvoicePayment() ? 'Factuurboeking veilig verwerken…' : 'Boeking veilig voorbereiden…')
            : 'Intake veilig verzenden…'
        );
      } else {
        this.submitLocked = false;
        this.updateSubmitState();
      }
    }

    async requestJSON(url, options = {}, timeout = REQUEST_TIMEOUT_MS) {
      const endpoint = this.normalizeEndpoint(url);
      if (!endpoint) throw new BookingError('invalid_endpoint', 'De API-koppeling is ongeldig.');

      const controller = new AbortController();
      const lifecycleAbort = () => controller.abort();
      const timer = window.setTimeout(() => controller.abort(), timeout);
      this.requestControllers.add(controller);
      this.lifecycle.signal.addEventListener('abort', lifecycleAbort, { once: true });

      try {
        const parsed = new URL(endpoint);
        const sameOrigin = parsed.origin === window.location.origin;
        const response = await fetch(endpoint, {
          ...options,
          mode: 'cors',
          credentials: sameOrigin ? 'same-origin' : 'omit',
          signal: controller.signal
        });
        const text = await response.text();
        let payload = {};
        if (text) {
          try {
            payload = JSON.parse(text);
          } catch (_) {
            payload = { message: text.slice(0, 500) };
          }
        }

        if (!response.ok) {
          throw new BookingError(
            this.errorCodeForStatus(response.status, payload),
            this.clean(payload?.message || payload?.error || payload?.description || 'De server kon de aanvraag niet verwerken.', 500),
            { status: response.status, payload }
          );
        }
        return payload;
      } catch (error) {
        if (error?.name === 'AbortError') {
          if (this.lifecycle.signal.aborted) throw error;
          throw new BookingError('network_timeout', 'De verbinding duurde te lang. Probeer het opnieuw.');
        }
        if (error instanceof BookingError) throw error;
        throw new BookingError(
          'network_error',
          navigator.onLine
            ? 'Er ging iets mis met de verbinding. Probeer het opnieuw.'
            : 'Je bent offline. Herstel je internetverbinding en probeer opnieuw.',
          { originalMessage: String(error?.message || '') }
        );
      } finally {
        window.clearTimeout(timer);
        this.lifecycle.signal.removeEventListener('abort', lifecycleAbort);
        this.requestControllers.delete(controller);
      }
    }

    errorCodeForStatus(status, payload) {
      const code = String(payload?.code || payload?.errorCode || payload?.error || '').toLowerCase();
      if (status === 409 || code.includes('slot')) return 'slot_unavailable';
      if (code === 'invoice_unavailable' || code === 'invoice_not_available_for_package') return code;
      if (status === 410) return 'expired';
      if (status === 422) return 'validation';
      if (status === 429) return 'rate_limited';
      return `http_${status}`;
    }

    // Small customer-facing translation layer. Raw server/provider strings are
    // retained only in the internal BookingError and are never rendered.
    customerError(error) {
      const code = String(error?.code || '');
      const status = Number(error?.details?.status || 0);
      if (code === 'slot_unavailable') return {
        title: 'Kies een ander trainingsmoment',
        message: 'Dit moment is waarschijnlijk net door iemand anders gekozen. Kies een ander moment en probeer het opnieuw.'
      };
      if (code === 'rate_limited') return {
        title: 'Probeer het over een moment opnieuw',
        message: 'Er zijn kort na elkaar te veel aanvragen gedaan. Wacht even en probeer het daarna opnieuw.'
      };
      if (code === 'network_timeout') return {
        title: 'Er ging iets mis',
        message: 'We konden deze stap niet afronden. Controleer je internetverbinding en probeer het opnieuw.'
      };
      if (code === 'network_error') return {
        title: 'Er ging iets mis',
        message: navigator.onLine
          ? 'We konden deze stap niet afronden. Probeer het opnieuw of neem contact met IdenScan op.'
          : 'Je bent offline. Herstel je internetverbinding en probeer het opnieuw.'
      };
      if (code.includes('availability_unavailable')) return {
        title: 'De planning kan nu niet worden geladen',
        message: 'IdenScan kan de beschikbare trainingsmomenten nu niet controleren. Probeer het later opnieuw of neem contact met IdenScan op.'
      };
      if (code === 'validation' || status === 422) return {
        title: 'Controleer je gegevens',
        message: 'Een of meer gegevens zijn nog niet helemaal compleet. Controleer de gemarkeerde velden en probeer het opnieuw.'
      };
      if (code.includes('email')) return {
        title: 'De bevestiging lukt nog niet',
        message: 'Controleer het e-mailadres en probeer het opnieuw. Blijft het probleem bestaan? Neem dan contact met IdenScan op.'
      };
      if (
        code === 'checkout_url_invalid'
        || code === 'checkout_response_invalid'
        || code === 'booking_response_invalid'
        || code === 'booking_api_missing'
        || code === 'checkout_api_missing'
      ) return {
        title: 'De betaling kon niet worden gestart',
        message: 'Probeer het opnieuw. Blijft het probleem bestaan? Neem dan contact met IdenScan op.'
      };
      if (
        code === 'invoice_api_missing'
        || code === 'invoice_response_invalid'
        || code === 'invoice_ui_missing'
        || code === 'invoice_unavailable'
        || code === 'invoice_not_available_for_package'
      ) return {
        title: 'De factuurboeking kan nu niet worden afgerond',
        message: 'Jullie gegevens zijn bewaard. Probeer het opnieuw of kies online aanbetaling.'
      };
      if (status >= 500 || code === 'http_502' || code === 'http_503' || code.includes('unavailable')) return {
        title: 'De trainingsaanvraag kan nu niet worden verwerkt',
        message: 'Probeer het over een moment opnieuw. Blijft het probleem bestaan? Neem dan contact met IdenScan op.'
      };
      if (status >= 400) return {
        title: 'Controleer je gegevens',
        message: 'Een of meer gegevens zijn nog niet helemaal compleet. Controleer de gemarkeerde velden en probeer het opnieuw.'
      };
      return {
        title: 'Er ging iets mis',
        message: 'We konden deze stap niet afronden. Probeer het opnieuw of neem contact met IdenScan op.'
      };
    }

    userFacingError(error) {
      return this.customerError(error).message;
    }

    normalizeEndpoint(value) {
      const source = String(value || '').trim();
      if (!source) return '';
      try {
        const url = new URL(source, window.location.origin);
        const local = ['localhost', '127.0.0.1'].includes(url.hostname);
        if (url.protocol !== 'https:' && !(local && url.protocol === 'http:')) return '';
        return url.toString();
      } catch (_) {
        return '';
      }
    }

    normalizePublicUrl(value) {
      const source = String(value || '').trim();
      if (!source) return '';
      try {
        const url = new URL(source, window.location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) return '';
        return url.toString();
      } catch (_) {
        return '';
      }
    }

    safeSameSiteUrl(value) {
      const source = String(value || '').trim();
      if (!source) return '';
      try {
        const url = new URL(source, window.location.origin);
        const allowed = url.origin === window.location.origin
          || url.hostname === 'idenscan.com'
          || url.hostname.endsWith('.idenscan.com')
          || url.hostname === 'idenscan-customer-dashboard.vercel.app';
        return allowed && url.protocol === 'https:' ? url.toString() : '';
      } catch (_) {
        return '';
      }
    }

    normalizeWebsite(value) {
      const source = this.clean(value, 200);
      if (!source) return '';
      try {
        const url = new URL(/^https?:\/\//i.test(source) ? source : `https://${source}`);
        return url.hostname.toLowerCase();
      } catch (_) {
        return source.toLowerCase();
      }
    }

    arrayStrings(value) {
      if (Array.isArray(value)) return value.map(String).map((item) => item.trim()).filter(Boolean);
      if (typeof value === 'string') return value.split(',').map((item) => item.trim()).filter(Boolean);
      return [];
    }

    selectedValues(name) {
      return qa(this.form, `input[name="${name}"]:checked`).map((input) => input.value);
    }

    value(name) {
      const field = this.form?.elements?.namedItem(name);
      if (!field) return '';
      if (field instanceof RadioNodeList) return String(field.value || '').trim();
      if (isField(field)) return String(field.value || '').trim();
      return '';
    }

    field(name) {
      const field = this.form?.elements?.namedItem(name);
      return isField(field) ? field : null;
    }

    money(cents) {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: this.config.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Math.max(0, int(cents)) / 100);
    }

    clean(value, maxLength = 500) {
      return String(value ?? '')
        .replace(/[\u0000-\u001F\u007F]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, maxLength);
    }

    safeToken(value, maxLength = 200) {
      return String(value || '').replace(/[^A-Za-z0-9._~-]/g, '').slice(0, maxLength);
    }

    safeReference(value) {
      return String(value || '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 200);
    }

    createId(prefix) {
      const random = typeof crypto?.randomUUID === 'function'
        ? crypto.randomUUID().replace(/-/g, '')
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
      return `${prefix}_${random}`.slice(0, 120);
    }

    scrollTo(element, offset = 0) {
      if (!element) return;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    toast(message, error = false) {
      const node = q(this.root, '[data-toast]');
      if (!node) return;
      window.clearTimeout(this.toastTimer);
      node.textContent = message;
      node.hidden = false;
      node.classList.toggle('is-error', error);
      node.setAttribute('role', error ? 'alert' : 'status');
      this.toastTimer = window.setTimeout(() => {
        node.hidden = true;
        node.classList.remove('is-error');
      }, error ? 7000 : 4500);
    }

    toggleAll(selector, show) {
      qa(this.root, selector).forEach((node) => { node.hidden = !show; });
    }

    hide(selector) {
      const node = q(this.root, selector);
      if (node) node.hidden = true;
    }

    setText(selector, value) {
      qa(this.root, selector).forEach((node) => { node.textContent = String(value ?? ''); });
    }

    dispatch(name, detail = {}) {
      this.root.dispatchEvent(new CustomEvent(`idenscan:training-booking:${name}`, {
        bubbles: true,
        detail: { version: VERSION, ...detail }
      }));
    }

    isLifecycleAbort(error) {
      return error?.name === 'AbortError' && this.lifecycle.signal.aborted;
    }

    failClosed(message) {
      qa(this.root, 'button, input, select, textarea').forEach((element) => {
        if (!(element instanceof HTMLInputElement && element.type === 'hidden')) {
          element.disabled = true;
        }
      });
      this.finalError(message);
      this.root.dataset.bookingFailed = 'true';
    }

    destroy() {
      this.lifecycle.abort();
      this.requestControllers.forEach((controller) => controller.abort());
      this.requestControllers.clear();
      window.clearTimeout(this.toastTimer);
      window.clearTimeout(this.draftTimer);
      this.root.removeAttribute('data-booking-initialized');
      instances.delete(this.root);
    }
  }

  function initialize(scope = document) {
    const roots = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...qa(scope, ROOT_SELECTOR));
    unique(roots).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new IdenScanTrainingBookingV5(root);
      if (instance.valid) instances.set(root, instance);
    });
  }

  function destroy(scope) {
    if (!(scope instanceof Element)) return;
    const roots = [];
    if (scope.matches(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...qa(scope, ROOT_SELECTOR));
    unique(roots).forEach((root) => instances.get(root)?.destroy());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target instanceof Element ? event.target : document);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target instanceof Element ? event.target : null);
  });
})();
